import { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import PhygitalIcon from "@/components/PhygitalIcon";
import LocationInput from "@/components/LocationInput";
import EnvelopeAnimation from "@/components/EnvelopeAnimation";
import { EditableText, EditableImage, EditableSection, useAdmin } from "@/components/AdminContentEditor";
import PageMeta from "@/components/PageMeta";
import { getCategoryPreset } from "@/lib/categoryConfig";
// Category-specific demo content components - exact replicas of landing pages
import MUADemoContent from "@/components/demos/MUADemoContent";
import HairDemoContent from "@/components/demos/HairDemoContent";
import NailDemoContent from "@/components/demos/NailDemoContent";
import CulinaryDemoContent from "@/components/demos/CulinaryDemoContent";
import LashDemoContent from "@/components/demos/LashDemoContent";
import {
  Sparkles,
  ArrowRight,
  Check,
  CheckCircle2,
  Users,
  Calendar,
  Image,
  MessageSquare,
  Share,
  Heart,
  Scissors,
  Palette,
  Leaf,
  PartyPopper,
  GraduationCap,
  Eye,
  Clock,
  Star,
  Zap,
  Gift,
  Instagram,
  Camera,
  MapPin,
  ChefHat,
  Play,
  FileText,
  Monitor,
  Send,
  ClipboardList,
  Bell,
  Plus,
  DollarSign,
  Cake
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// Image-based category icons from uploaded assets
const HairImgIcon = (props) => <img src="/category-icons/hair.png" alt="Hair" style={{ width: 16, height: 16, objectFit: 'contain' }} {...props} />;
const LashImgIcon = (props) => <img src="/category-icons/lash.png" alt="Lash" style={{ width: 16, height: 16, objectFit: 'contain' }} {...props} />;
const MakeupImgIcon = (props) => <img src="/category-icons/makeup.png" alt="Makeup" style={{ width: 16, height: 16, objectFit: 'contain' }} {...props} />;
const NailImgIcon = (props) => <img src="/category-icons/nail.png" alt="Nail" style={{ width: 16, height: 16, objectFit: 'contain' }} {...props} />;
const EventsImgIcon = (props) => <img src="/category-icons/events.png" alt="Events" style={{ width: 16, height: 16, objectFit: 'contain' }} {...props} />;
const WellnessImgIcon = (props) => <img src="/category-icons/wellness.png" alt="Wellness" style={{ width: 16, height: 16, objectFit: 'contain' }} {...props} />;
const CulinaryImgIcon = (props) => <img src="/category-icons/culinary.png" alt="Culinary" style={{ width: 16, height: 16, objectFit: 'contain' }} {...props} />;
const InstructorImgIcon = (props) => <img src="/category-icons/instructor.png" alt="Instructor" style={{ width: 16, height: 16, objectFit: 'contain' }} {...props} />;
const DispensaryImgIcon = (props) => <img src="/category-icons/dispensary.png" alt="Dispensary" style={{ width: 16, height: 16, objectFit: 'contain' }} {...props} />;
const TravelImgIcon = (props) => <img src="/category-icons/travel.png" alt="Travel" style={{ width: 16, height: 16, objectFit: 'contain' }} {...props} />;
const RetailImgIcon = (props) => <img src="/category-icons/retail.png" alt="Retail" style={{ width: 16, height: 16, objectFit: 'contain' }} {...props} />;

// Rotating words with color gradients (same as HomePage/PitchDeck)
const ROTATING_WORDS = [
  { word: "hair", color: "from-pink-500 to-purple-500" },
  { word: "nail", color: "from-pink-400 to-rose-500" },
  { word: "makeup", color: "from-pink-500 to-rose-500" },
  { word: "lash", color: "from-violet-500 to-purple-500" },
  { word: "wellness", color: "from-emerald-500 to-teal-500" },
  { word: "event", color: "from-amber-500 to-orange-500" },
  { word: "culinary", color: "from-orange-500 to-red-500" },
  { word: "instructor", color: "from-blue-500 to-sky-500" }
];

// Pilot type configurations with FULL demo content
const PILOT_CONFIGS = {
  hair: {
    title: "Hair",
    category: "beauty",
    icon: HairImgIcon,
    color: "from-pink-500 to-purple-500",
    bgLight: "from-pink-50 to-purple-50",
    hex: "#ec4899", // Pink primary
    features: [
      "Client preference cards with hair history",
      "Service menu with add-ons & pricing",
      "Appointment prep reminders",
      "Portfolio showcase"
    ],
    description: "Built for stylists who want to know their clients before they sit down.",
    // Full demo content matching /hair page
    services: [
      { name: "Box Braids", price: "$150+" },
      { name: "Knotless Braids", price: "$200+" },
      { name: "Twists", price: "$120+" },
      { name: "Locs Maintenance", price: "$80+" },
      { name: "Silk Press", price: "$85+" },
      { name: "Color", price: "$100+" }
    ],
    addOns: ["Edge Control", "Deep Conditioning", "Scalp Treatment", "Trim", "Style Finishing"],
    clientPreferences: ["Length", "Texture", "Color History", "Allergies", "Preferred Products"],
    bookingFlow: ["Browse Services", "Select Add-ons", "Share Preferences", "Confirm Time", "Get Prepped"]
  },
  nail: {
    title: "Nail",
    category: "beauty",
    icon: NailImgIcon,
    color: "from-pink-400 to-rose-500",
    bgLight: "from-rose-50 to-pink-50",
    hex: "#f472b6", // Pink-400
    features: [
      "Style inspiration sharing",
      "Nail shape & length preferences",
      "Allergy & sensitivity tracking",
      "Design reference boards"
    ],
    description: "Know exactly what your client wants before they arrive.",
    services: [
      { name: "Classic Manicure", price: "$30+" },
      { name: "Gel Manicure", price: "$45+" },
      { name: "Acrylic Full Set", price: "$65+" },
      { name: "Nail Art", price: "$15+" },
      { name: "Pedicure", price: "$50+" },
      { name: "Dip Powder", price: "$55+" }
    ],
    addOns: ["Nail Art", "Gel Polish", "Soak Off", "Repair", "Hand Massage"],
    clientPreferences: ["Shape", "Length", "Design Style", "Allergies", "Nail Conditions"],
    bookingFlow: ["Select Service", "Choose Shape", "Share Inspo", "Book Time", "Get Glam"]
  },
  makeup: {
    title: "Makeup",
    category: "beauty",
    icon: MakeupImgIcon,
    color: "from-pink-500 to-rose-500",
    bgLight: "from-pink-50 to-rose-50",
    heroText: "Know Your Client's Look Before They Sit Down",
    heroSubtext: "Keep using Acuity for booking. Phygital adds a preference layer that syncs client details, skin info, and inspo photos - before the appointment.",
    hex: "#f472b6", // Pink
    features: [
      "Works with Acuity",
      "Inspo before booking",
      "Change notifications",
      "Look approval flow"
    ],
    description: "Your preferences, perfectly communicated.",
    services: [
      { name: "In Studio", price: "$150+" },
      { name: "On Location", price: "$250+" },
      { name: "Bridal", price: "$300+" },
      { name: "Special Event", price: "$175+" }
    ],
    addOns: ["Lash Application", "Brow Styling", "Setting Spray", "Contouring", "Color Matching"],
    clientPreferences: ["Skin Type", "Undertone", "Coverage Level", "Allergies"],
    bookingFlow: [
      { step: "Book on Acuity", desc: "Client books their appointment" },
      { step: "Phygital Prompt", desc: "Auto-text with preference link" },
      { step: "MUA Reviews", desc: "You see everything before they arrive" },
      { step: "Day Of", desc: "Client arrives, you're ready to glow" }
    ],
    testimonial: {
      quote: "I used to spend 20 minutes on consultations. Now I already know everything before they sit down.",
      author: "Naomi T.",
      role: "Pro MUA, Los Angeles"
    }
  },
  lash: {
    title: "Lash",
    category: "beauty",
    icon: LashImgIcon,
    color: "from-violet-500 to-purple-500",
    bgLight: "from-violet-50 to-purple-50",
    heroText: "Know Their Ideal Look Before They Close Their Eyes",
    heroSubtext: "Keep using Acuity for booking. Phygital adds a preference layer so clients share eye shape, style, and inspo photos - before the appointment.",
    hex: "#8b5cf6", // Violet-500
    features: [
      "Works with Acuity",
      "Eye shape & lash mapping",
      "Allergy & sensitivity tracking",
      "Style preference library"
    ],
    description: "Know their ideal lash look before they close their eyes.",
    services: [
      { name: "Classic Full Set", price: "$120+" },
      { name: "Volume Full Set", price: "$180+" },
      { name: "Hybrid Full Set", price: "$150+" },
      { name: "Mega Volume", price: "$220+" },
      { name: "Lash Fill (2 weeks)", price: "$65+" },
      { name: "Lash Fill (3 weeks)", price: "$85+" },
      { name: "Lash Lift & Tint", price: "$80+" }
    ],
    addOns: ["Colored Lashes", "Bottom Lashes", "Brow Lamination", "Lash Bath", "Removal"],
    clientPreferences: ["Eye Shape", "Natural Lash Length", "Style Preference", "Curl Type", "Length Map", "Sensitivities"],
    bookingFlow: [
      { step: "Book on Acuity", desc: "Client books their lash appointment" },
      { step: "Phygital Prompt", desc: "Auto-text with preference link" },
      { step: "Lash Tech Reviews", desc: "See eye shape, style & inspo before they arrive" },
      { step: "Day Of", desc: "Client arrives, you're prepped with the perfect lash map" }
    ]
  },
  wellness: {
    title: "Wellness",
    category: "beauty",
    icon: WellnessImgIcon,
    color: "from-emerald-500 to-teal-500",
    bgLight: "from-emerald-50 to-teal-50",
    hex: "#10b981", // Emerald-500
    features: [
      "Health & comfort preferences",
      "Pressure & technique preferences",
      "Aromatherapy selections",
      "Session history tracking"
    ],
    description: "Deliver truly personalized wellness experiences.",
    services: [
      { name: "Swedish Massage", price: "$90+" },
      { name: "Deep Tissue", price: "$110+" },
      { name: "Hot Stone", price: "$130+" },
      { name: "Facial", price: "$95+" },
      { name: "Body Wrap", price: "$120+" },
      { name: "Reiki", price: "$85+" }
    ],
    addOns: ["Aromatherapy", "Hot Towels", "Scalp Massage", "Extended Time", "CBD Enhancement"],
    clientPreferences: ["Pressure Level", "Problem Areas", "Scent Preferences", "Health Conditions", "Temp Comfort"],
    bookingFlow: ["Select Service", "Share Health Info", "Set Preferences", "Book Session", "Relax"]
  },
  event: {
    title: "Event",
    category: "event",
    icon: EventsImgIcon,
    color: "from-amber-500 to-orange-500",
    bgLight: "from-amber-50 to-orange-50",
    hex: "#f59e0b", // Amber-500
    features: [
      "Guest management & VIP lists",
      "RSVP tracking & confirmations",
      "Real-time check-in",
      "Dietary & accessibility tracking"
    ],
    description: "Streamlined event management with VIP lists and real-time check-in.",
    services: [
      { name: "Guest Check-in", price: "Included" },
      { name: "VIP Management", price: "Included" },
      { name: "RSVP Tracking", price: "Included" },
      { name: "Seating Charts", price: "Premium" },
      { name: "Dietary Tracking", price: "Included" },
      { name: "Badge Printing", price: "Premium" }
    ],
    addOns: ["Photo Booth Integration", "Live Polling", "Digital Swag Bags", "Post-Event Analytics"],
    clientPreferences: ["Dietary Needs", "Accessibility", "Seating Preference", "Plus One Info", "Photo Consent"],
    bookingFlow: ["Create Event", "Send Invites", "Collect RSVPs", "Manage Day-Of", "Follow Up"]
  },
  instructor: {
    title: "Instructor",
    category: "instructor",
    icon: InstructorImgIcon,
    color: "from-blue-500 to-sky-500",
    bgLight: "from-blue-50 to-sky-50",
    hex: "#3b82f6", // Blue-500
    headline: "teaching preferences, perfectly communicated.",
    subHeadline: "Know your students' learning styles, skill levels, and goals before the first session. Transform how you teach.",
    features: [
      "Personalized from day one - Know their learning style, goals, and challenges before you meet",
      "Track progress - Document milestones, share feedback, and celebrate wins together",
      "Build your reputation - Students remember educators who truly understand them"
    ],
    description: "Built for every type of educator - adapts to your teaching style.",
    educatorTypes: [
      { type: "Workshop Leaders", description: "Teaches hands-on skills to groups", specialties: ["Beauty educators", "Craft instructors", "Culinary classes", "DIY workshops"] },
      { type: "1-on-1 Instructors", description: "Personalized individual coaching", specialties: ["Music lessons", "Tutoring", "Fitness training", "Language coaching"] },
      { type: "Speakers & Presenters", description: "Engages audiences at events", specialties: ["Keynote speakers", "Seminar hosts", "Panelists"] },
      { type: "Community Educators", description: "Teaches in community settings", specialties: ["Library programs", "Community centers", "Non-profit education"] }
    ],
    services: [
      { name: "1:1 Sessions", price: "$75+/hr" },
      { name: "Group Classes", price: "$25+/person" },
      { name: "Workshops", price: "$150+" },
      { name: "Certification", price: "Varies" },
      { name: "Online Courses", price: "$50+" },
      { name: "Mentorship", price: "$200+/mo" }
    ],
    addOns: ["Materials Included", "Recording Access", "Certificate", "Follow-up Q&A", "Resource Pack"],
    clientPreferences: ["Experience level", "Learning style", "Class format", "Goals"],
    experienceLevels: ["Complete beginner", "Some experience", "Intermediate", "Advanced"],
    learningStyles: ["Visual", "Hands-on", "Read/write", "Auditory"],
    classFormats: ["In-person", "Virtual", "Hybrid"],
    studentGoals: ["Hobby", "Career change", "Skill upgrade", "Professional development"],
    bookingFlow: [
      { step: 1, title: "Set up your profile", description: "Define your teaching style, services, and what you need to know about students" },
      { step: 2, title: "Students share preferences", description: "Learners fill out their preference card with learning style, goals, and context" },
      { step: 3, title: "Teach with context", description: "Start every session knowing exactly how to help them succeed" }
    ],
    benefits: [
      { title: "Personalized from day one", description: "No more guessing. Know their learning style, goals, and challenges before you meet." },
      { title: "Track progress", description: "Document milestones, share feedback, and celebrate wins together." },
      { title: "Build your reputation", description: "Students remember educators who truly understand them." }
    ]
  },
  culinary: {
    title: "Culinary",
    category: "culinary",
    icon: CulinaryImgIcon,
    color: "from-orange-500 to-red-500",
    bgLight: "from-orange-50 to-red-50",
    hex: "#f97316", // Orange-500
    features: [
      "Dietary restrictions & allergies",
      "Cuisine preferences & dislikes",
      "Custom menu planning",
      "Guest taste profiles"
    ],
    description: "Know your guests' tastes before they taste your food.",
    services: [
      { name: "Private Dinner", price: "$150+/person" },
      { name: "Custom Cake", price: "$75+" },
      { name: "Meal Prep", price: "$200+/week" },
      { name: "Event Catering", price: "Quote" },
      { name: "Cooking Class", price: "$100+" },
      { name: "Tasting Menu", price: "$250+" }
    ],
    addOns: ["Wine Pairing", "Dessert Course", "Take-home Portions", "Recipe Cards", "Dietary Customization"],
    clientPreferences: ["Dietary Needs", "Allergies", "Cuisine Style", "Spice Level", "Portion Size"],
    bookingFlow: ["Choose Service", "Share Dietary Info", "Review Menu", "Confirm Date", "Enjoy"]
  },
  dispensary: {
    title: "Dispensary",
    category: "dispensary",
    icon: DispensaryImgIcon,
    color: "from-green-500 to-emerald-500",
    bgLight: "from-green-50 to-emerald-50",
    hex: "#22c55e", // Green-500
    features: [
      "Product preference tracking",
      "Consumption history",
      "Effect preferences",
      "Personalized recommendations"
    ],
    description: "Personalized cannabis experiences based on individual preferences.",
    services: [
      { name: "In-Store Pickup", price: "Varies" },
      { name: "Delivery", price: "Varies" },
      { name: "Consultation", price: "Free" },
      { name: "Loyalty Program", price: "Points" }
    ],
    addOns: ["Gift Wrapping", "Accessories", "Educational Materials"],
    clientPreferences: ["Preferred Effects", "Consumption Method", "Strength Preference", "Flavor Profile"],
    bookingFlow: ["Browse Menu", "Share Preferences", "Get Recommendations", "Order", "Enjoy"]
  },
  travel: {
    title: "Travel & Hospitality",
    category: "travel",
    icon: TravelImgIcon,
    color: "from-sky-500 to-blue-500",
    bgLight: "from-sky-50 to-blue-50",
    hex: "#0ea5e9", // Sky-500
    features: [
      "Guest preference profiles",
      "Room & seat customization",
      "Dietary needs tracking",
      "Loyalty experience management"
    ],
    description: "Personalized hospitality for airlines, hotels, and travel agencies.",
    services: [
      { name: "Room Booking", price: "Varies", icon: "🏨" },
      { name: "Flight Booking", price: "Varies", icon: "✈️" },
      { name: "Concierge", price: "Included", icon: "🎩" },
      { name: "Dining Reservations", price: "Free", icon: "🍽️" },
      { name: "Experience Packages", price: "Varies", icon: "🎁" },
      { name: "Airport Transfer", price: "$50+", icon: "🚗" }
    ],
    addOns: ["Late Checkout", "Room Upgrade", "Welcome Amenities", "Airport Lounge", "Private Tours"],
    clientPreferences: ["Room Type", "Bed Preference", "Floor Level", "Dietary Needs", "Special Occasions"],
    bookingFlow: ["Search", "Share Preferences", "Select Options", "Book", "Travel"]
  }
};

// Pilot categories for multi-select
const PILOT_CATEGORIES = [
  { id: "hair", label: "Hair", icon: HairImgIcon },
  { id: "nail", label: "Nail", icon: NailImgIcon },
  { id: "makeup", label: "Makeup", icon: MakeupImgIcon },
  { id: "lash", label: "Lash", icon: LashImgIcon },
  { id: "wellness", label: "Wellness / Spa", icon: WellnessImgIcon },
  { id: "event", label: "Events", icon: EventsImgIcon },
  { id: "culinary", label: "Culinary / Chef", icon: CulinaryImgIcon },
  { id: "instructor", label: "Instructor / Educator", icon: InstructorImgIcon }
];

export default function PilotInvitePage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { editMode } = useAdmin();
  
  // Invite data from backend
  const [inviteData, setInviteData] = useState(null);
  const [subCategorySettings, setSubCategorySettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Envelope animation state
  const [showEnvelope, setShowEnvelope] = useState(true);
  
  // Active tab state - "overview" or "demo" or specific demo categories
  const [activeTab, setActiveTab] = useState("overview");
  const [activeDemoTab, setActiveDemoTab] = useState(null);
  
  // Rotating word animation
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  
  // RSVP form state
  const [showRsvpForm, setShowRsvpForm] = useState(false);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    businessName: "",
    city: "",
    pilotCategories: [],
    isSolo: true,
    teamCategories: []
  });
  const [submitting, setSubmitting] = useState(false);

  // Handle envelope animation complete
  const handleEnvelopeComplete = () => {
    setTimeout(() => setShowEnvelope(false), 500);
  };

  // Fetch invite data - try both endpoints
  useEffect(() => {
    const fetchInvite = async () => {
      try {
        // Try portal invite endpoint first (new system)
        let res = await fetch(`${API}/api/portal/invite/${token}`);
        
        if (res.ok) {
          const data = await res.json();
          // Convert portal invite format to expected format
          const invite = data.invite || {};
          const subCat = data.sub_category || {};
          
          // Store FULL sub-category data including phase_templates at root level
          // and settings as a nested object - combine all sources
          setSubCategorySettings({
            ...subCat.settings,
            // Include phase_templates from root level (where they're actually stored)
            phase_templates: subCat.phase_templates || subCat.settings?.phase_templates || {},
            // Include branding from root level
            branding: subCat.branding || subCat.settings?.branding || {},
            // Include invite hero image if stored
            invite_hero_image: subCat.invite_hero_image || subCat.settings?.invite_hero_image
          });
          
          setInviteData({
            ...invite,
            guest_name: invite.name,
            first_name: invite.first_name || invite.name?.split(" ")[0] || "",
            // Use preset_id from sub_category - NO hardcoded defaults
            pilot_type: subCat.preset_id || subCat.category_type || invite.sub_category_name?.toLowerCase() || null,
            // Store preset_id separately for demo content
            preset_id: subCat.preset_id,
            // Category-level canonical branding from API (admin-controlled)
            category_branding: data.category_branding || {},
            envelope_icon_url: data.envelope_icon_url || data.category_branding?.icon_url || "",
            // Store envelope/branding - INVITE images override sub_category defaults; final fallback is canonical category dark hex
            background_image: invite.background_image || subCat.phase_templates?.envelope?.background_image || subCat.settings?.phase_templates?.envelope?.background_image,
            cover_image: invite.cover_image || subCat.phase_templates?.envelope?.cover_image || subCat.settings?.phase_templates?.envelope?.cover_image,
            envelope_bg_color: subCat.phase_templates?.envelope?.bg_color || subCat.settings?.phase_templates?.envelope?.bg_color || data.envelope_bg_color || data.category_branding?.dark_hex,
            prefill: {
              first_name: invite.first_name || invite.prefill?.first_name || invite.name?.split(" ")[0] || "",
              last_name: invite.last_name || invite.prefill?.last_name || invite.name?.split(" ").slice(1).join(" ") || "",
              email: invite.email || invite.prefill?.email || "",
              phone: invite.phone || invite.prefill?.phone || "",
              business_name: invite.company_name || invite.prefill?.business_name || "",
              city: invite.city || invite.prefill?.city || "",
              // Include admin pre-filled data (overrides if set)
              ...invite.prefill
            }
          });
          
          // Pre-fill form (use prefill object as authoritative source for already-known portals)
          const pf = invite.prefill || {};
          setFormData(prev => ({
            ...prev,
            firstName: pf.first_name || invite.first_name || invite.name?.split(" ")[0] || "",
            lastName: pf.last_name || invite.last_name || invite.name?.split(" ").slice(1).join(" ") || "",
            email: pf.email || invite.email || "",
            phone: pf.phone || invite.phone || "",
            businessName: pf.business_name || invite.company_name || "",
            city: pf.city || invite.city || "",
            // Use actual category from sub_category, not hardcoded default
            pilotCategories: [subCat.preset_id || subCat.name?.toLowerCase()].filter(Boolean)
          }));
          
          setLoading(false);
          return;
        }
        
        // Fallback to old pilot invite endpoint
        res = await fetch(`${API}/api/pilot/invite/${token}`);
        if (!res.ok) {
          throw new Error("Invalid or expired invite");
        }
        const data = await res.json();
        // Merge canonical category branding into inviteData for envelope
        setInviteData({
          ...data,
          category_branding: data.category_branding || {},
          envelope_bg_color: data.envelope_bg_color || data.category_branding?.dark_hex,
          envelope_icon_url: data.envelope_icon_url || data.category_branding?.icon_url || "",
        });
        
        // Pre-fill form if data exists
        if (data.prefill) {
          setFormData(prev => ({
            ...prev,
            firstName: data.prefill.first_name || "",
            lastName: data.prefill.last_name || "",
            email: data.prefill.email || "",
            phone: data.prefill.phone || "",
            businessName: data.prefill.business_name || "",
            city: data.prefill.city || ""
          }));
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    
    if (token) {
      fetchInvite();
    } else {
      setLoading(false);
    }
  }, [token]);

  // Rotating words animation - Show portal's category first for longer, then rotate
  const [hasShownFirstWord, setHasShownFirstWord] = useState(false);
  
  useEffect(() => {
    // First word (portal's own category) shows for 10 seconds
    // Subsequent words rotate every 4 seconds
    const firstWordDelay = 10000; // 10 seconds for first word
    const normalDelay = 4000; // 4 seconds for others
    
    let timeout;
    
    const rotateWord = () => {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentWordIndex((prev) => {
          const nextIndex = (prev + 1) % ROTATING_WORDS.length;
          return nextIndex;
        });
        setIsAnimating(false);
        setHasShownFirstWord(true);
      }, 400);
    };
    
    // Use longer delay for first word, normal delay for others
    const delay = !hasShownFirstWord && currentWordIndex === 0 ? firstWordDelay : normalDelay;
    timeout = setTimeout(rotateWord, delay);

    return () => clearTimeout(timeout);
  }, [currentWordIndex, hasShownFirstWord]);

  // Normalize pilot type to match PILOT_CONFIGS keys
  const normalizeType = (type) => {
    if (!type) return null; // Return null instead of defaulting to 'hair'
    const typeMap = {
      'hair-stylists': 'hair',
      'nail-technicians': 'nail',
      'nail-techs': 'nail',
      'nails': 'nail',
      'lash-artists': 'lash',
      'makeup-artists': 'makeup',
      'makeup': 'makeup',
      'mua': 'makeup',
      'lash': 'lash',
      'lashes': 'lash',
      'eyelash': 'lash',
      'eyelashes': 'lash',
      'hair': 'hair',
      'event': 'event',
      'events': 'event',
      'culinary': 'culinary',
      'chef': 'culinary',
      'instructor': 'instructor',
      'wellness': 'wellness'
    };
    return typeMap[type?.toLowerCase()] || type?.toLowerCase();
  };

  // Define pilotType and config before using them
  const pilotType = normalizeType(inviteData?.pilot_type);
  
  // Default fallback config when pilotType is unknown
  const DEFAULT_CONFIG = {
    title: pilotType ? pilotType.charAt(0).toUpperCase() + pilotType.slice(1) : 'Pilot',
    color: 'from-purple-500 to-indigo-600',
    bgLight: 'from-purple-50 to-indigo-50',
    icon: Sparkles,
    features: [],
    services: [],
    addOns: [],
    bookingFlow: [],
    clientPreferences: []
  };
  
  // Use pilotType config if available, otherwise use default
  const config = pilotType ? (PILOT_CONFIGS[pilotType] || DEFAULT_CONFIG) : DEFAULT_CONFIG;

  // Set activeDemoTab when pilotType is determined
  useEffect(() => {
    if (pilotType && !activeDemoTab) {
      setActiveDemoTab(pilotType);
    }
  }, [pilotType, activeDemoTab]);

  // Order words with the current category FIRST - memoized to prevent recalculation
  const orderedWords = useMemo(() => {
    if (!pilotType) return ROTATING_WORDS;
    
    // Find the index of the pilotType (case-insensitive)
    const catIndex = ROTATING_WORDS.findIndex(w => 
      w.word.toLowerCase() === pilotType?.toLowerCase() || 
      w.word.toLowerCase() === config?.title?.toLowerCase()
    );
    
    // If not found, return original order
    if (catIndex === -1) return ROTATING_WORDS;
    
    // Put the current category first, then the rest in order
    return [...ROTATING_WORDS.slice(catIndex), ...ROTATING_WORDS.slice(0, catIndex)];
  }, [pilotType, config?.title]);

  // Reset word index when pilotType changes (so portal's category shows first)
  useEffect(() => {
    if (pilotType) {
      setCurrentWordIndex(0);
      setHasShownFirstWord(false);
    }
  }, [pilotType]);

  const currentWord = orderedWords[currentWordIndex % orderedWords.length];
  
  const [envelopeOpening, setEnvelopeOpening] = useState(false);

  const handleOpenEnvelope = () => {
    setEnvelopeOpening(true);
    setTimeout(() => setShowEnvelope(false), 1200);
  };

  // Auto-open after 3 seconds if user doesn't click (only if envelope animation is shown)
  useEffect(() => {
    if (showEnvelope && inviteData && !envelopeOpening) {
      const timer = setTimeout(() => {
        handleEnvelopeComplete();
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [showEnvelope, inviteData, envelopeOpening]);

  // Handle category toggle
  const toggleCategory = (categoryId) => {
    setFormData(prev => ({
      ...prev,
      pilotCategories: prev.pilotCategories.includes(categoryId)
        ? prev.pilotCategories.filter(c => c !== categoryId)
        : [...prev.pilotCategories, categoryId]
    }));
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      const res = await fetch(`${API}/api/pilot/invite/${token}/accept`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          first_name: formData.firstName,
          last_name: formData.lastName,
          email: formData.email,
          phone: formData.phone,
          business_name: formData.businessName,
          city: formData.city,
          pilot_categories: formData.pilotCategories,
          is_solo: formData.isSolo,
          team_categories: formData.teamCategories
        })
      });
      
      if (!res.ok) {
        throw new Error("Failed to submit");
      }
      
      const data = await res.json();
      
      // Store session token
      if (data.session_token) {
        localStorage.setItem("session_token", data.session_token);
      }
      
      // Navigate to welcome page (or use redirect_url if provided)
      const redirectUrl = data.redirect_url || `/pilot/welcome/${data.portal_id || token}`;
      navigate(redirectUrl);
      
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-2 border-gray-200 border-t-black rounded-full animate-spin" />
          <p className="text-gray-500 font-mono text-sm">Loading invite...</p>
        </div>
      </div>
    );
  }

  // If no token or error, show generic invite page
  const guestName = inviteData?.first_name || inviteData?.prefill?.first_name || inviteData?.guest_name?.split(" ")[0] || "";

  // Get demo page URL for this category type
  const getDemoUrl = () => {
    const categoryMap = {
      'hair': '/hair',
      'makeup': '/mua',
      'nail': '/nail',
      'nails': '/nail',
      'wellness': '/wellness',
      'event': '/events',
      'events': '/events',
      'instructor': '/instructor',
      'culinary': '/culinary'
    };
    return categoryMap[pilotType] || `/${pilotType || 'pilots'}`;
  };

  return (
    <div className="min-h-screen bg-white">
      <PageMeta 
        title={`${config?.title || pilotType?.charAt(0).toUpperCase() + pilotType?.slice(1) || 'Pilot'} Pilot Invitation`}
        description={config?.description || `You're invited to join the ${config?.title || pilotType} pilot program by Phygital`}
        image={inviteData?.cover_image || subCategorySettings?.phase_templates?.envelope?.cover_image || subCategorySettings?.invite_hero_image}
      />
      {/* Envelope Intro Animation - Uses new component with invite customizations */}
      {showEnvelope && (
        <EnvelopeAnimation
          onComplete={handleEnvelopeComplete}
          coverImage={inviteData?.cover_image || subCategorySettings?.phase_templates?.envelope?.cover_image || subCategorySettings?.invite_hero_image}
          logoImage={inviteData?.host_logo_url}
          backgroundImage={inviteData?.background_image || subCategorySettings?.phase_templates?.envelope?.background_image}
          iconUrl={inviteData?.envelope_icon_url || inviteData?.category_branding?.icon_url}
          recipientName={guestName}
          senderName={inviteData?.sender_name || "Phygital"}
          pilotName={config?.title ? `${config.title} Pilot` : (pilotType ? `${pilotType.charAt(0).toUpperCase() + pilotType.slice(1)} Pilot` : "Pilot")}
          inviteText={inviteData?.envelope_text || subCategorySettings?.phase_templates?.envelope?.text || "YOU'RE INVITED"}
          bgColor={inviteData?.envelope_bg_color || subCategorySettings?.phase_templates?.envelope?.bg_color || inviteData?.category_branding?.dark_hex}
          textColor={inviteData?.envelope_text_color || subCategorySettings?.phase_templates?.envelope?.text_color || "#ffffff"}
          categoryId={inviteData?.category_id || `cat_${pilotType}`}
        />
      )}
      
      {/* Main content fades in after envelope */}
      <div className={`transition-opacity duration-500 ${showEnvelope ? "opacity-0" : "opacity-100"}`}>
      {/* Header - Logo clickable to home */}
      <header className="border-b bg-white/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <a href="/home" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
            <PhygitalIcon className="w-8 h-8" variant="light" />
            <span className="font-semibold text-xl">phygital</span>
          </a>
          <Badge variant="outline" className={`bg-gradient-to-r ${config?.bgLight || 'bg-gray-50'} border-0`}>
            {config?.icon ? <config.icon className="w-3 h-3 mr-1" /> : null}
            {config?.title || (pilotType ? pilotType.charAt(0).toUpperCase() + pilotType.slice(1) : 'Pilot')}
          </Badge>
        </div>
        
        {/* Page Tabs - Overview vs Demo tabs from invite */}
        <div className="border-t bg-white">
          <div className="max-w-6xl mx-auto px-4">
            <div className="flex gap-1 flex-wrap">
              <button
                onClick={() => { setActiveTab("overview"); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                className={`px-4 py-3 text-sm font-medium flex items-center gap-2 border-b-2 transition-colors ${
                  activeTab === "overview" 
                    ? `border-purple-600 text-purple-600` 
                    : "border-transparent text-gray-500 hover:text-gray-700"
                }`}
              >
                <FileText className="w-4 h-4" />
                Overview
              </button>
              {/* Render demo tabs - PRIMARY pilot category FIRST, then any additional from demo_tabs */}
              {(() => {
                // Get unique demo tabs with primary pilot type always first
                const additionalTabs = (inviteData?.demo_tabs || []).filter(t => t !== pilotType);
                const allDemoTabs = [pilotType, ...additionalTabs];
                
                // Color mapping for each category
                const categoryColors = {
                  makeup: { bg: 'bg-pink-500', border: 'border-pink-500', text: 'text-pink-500', hover: 'hover:bg-pink-50' },
                  mua: { bg: 'bg-pink-500', border: 'border-pink-500', text: 'text-pink-500', hover: 'hover:bg-pink-50' },
                  hair: { bg: 'bg-purple-500', border: 'border-purple-500', text: 'text-purple-500', hover: 'hover:bg-purple-50' },
                  nail: { bg: 'bg-rose-500', border: 'border-rose-500', text: 'text-rose-500', hover: 'hover:bg-rose-50' },
                  lash: { bg: 'bg-violet-500', border: 'border-violet-500', text: 'text-violet-500', hover: 'hover:bg-violet-50' },
                  wellness: { bg: 'bg-emerald-500', border: 'border-emerald-500', text: 'text-emerald-500', hover: 'hover:bg-emerald-50' },
                  event: { bg: 'bg-amber-500', border: 'border-amber-500', text: 'text-amber-500', hover: 'hover:bg-amber-50' },
                  instructor: { bg: 'bg-blue-500', border: 'border-blue-500', text: 'text-blue-500', hover: 'hover:bg-blue-50' },
                  culinary: { bg: 'bg-orange-500', border: 'border-orange-500', text: 'text-orange-500', hover: 'hover:bg-orange-50' },
                };
                
                return allDemoTabs.map(demoType => {
                  const demoConfig = PILOT_CONFIGS[demoType] || PILOT_CONFIGS[pilotType] || null;
                  const DemoIcon = demoConfig?.icon || config?.icon || FileText;
                  const colors = categoryColors[demoType] || categoryColors[pilotType] || categoryColors.event;
                  const isActive = activeTab === "demo" && activeDemoTab === demoType;
                  
                  return (
                    <button
                      key={demoType}
                      onClick={() => { setActiveTab("demo"); setActiveDemoTab(demoType); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                      className={`p-3 rounded-lg transition-all ${
                        isActive
                          ? `${colors.bg} text-white shadow-md` 
                          : `bg-gray-100 ${colors.text} ${colors.hover}`
                      }`}
                      data-testid={`demo-tab-${demoType}`}
                      title={`${demoConfig?.title || demoType} Demo`}
                    >
                      <DemoIcon className="w-5 h-5" />
                    </button>
                  );
                });
              })()}
            </div>
          </div>
        </div>
      </header>

      {/* Tab Content */}
      {activeTab === "overview" ? (
        <>
          {/* Hero Section */}
          <EditableSection sectionId={`invite-hero-${pilotType || 'pilot'}`} label={`${config?.title || 'Pilot'} Hero`} className={editMode ? "relative overflow-hidden py-12 sm:py-20 px-4" : "relative overflow-hidden py-12 sm:py-20 px-4"}>
            {/* Background - Use CMS image if available, fallback to category settings */}
            {subCategorySettings?.invite_hero_image ? (
              <div 
                className="absolute inset-0 bg-cover bg-center"
                style={{ 
                  backgroundImage: `url(${subCategorySettings.invite_hero_image})`,
                  filter: 'brightness(0.9)'
                }}
              >
                <div className="absolute inset-0 bg-gradient-to-b from-white/80 via-white/60 to-white/90" />
              </div>
            ) : (
              <>
                <div className={`absolute inset-0 bg-gradient-to-br ${config.bgLight}`} />
                <div className="absolute inset-0 opacity-20">
                  <div className="absolute top-20 left-20 w-64 h-64 bg-pink-200 rounded-full blur-3xl" />
                  <div className="absolute bottom-20 right-20 w-80 h-80 bg-purple-200 rounded-full blur-3xl" />
                </div>
              </>
            )}
            
            <div className="relative max-w-4xl mx-auto text-center px-4">
              {/* Main heading with rotating gradient word - "perfectly communicated" on its own line */}
              <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold mb-4 tracking-tight">
                <span
                  className={`inline transition-opacity duration-[400ms] bg-gradient-to-r ${currentWord.color} bg-clip-text text-transparent ${
                    isAnimating ? "opacity-0" : "opacity-100"
                  }`}
                >
                  {currentWord.word}
                </span>{" "}
                <span className="text-gray-900">preferences,</span>
                <br />
                <span className="text-gray-900 whitespace-nowrap">perfectly communicated.</span>
              </h1>
              
              {/* Currently Invite Only Badge */}
              <p className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-6">
                <EditableText
                  sectionId={`invite-hero-${pilotType}`}
                  field="badge_text"
                  defaultValue="Currently Invite Only"
                  className="inline"
                />
              </p>
              
              {/* Personalized Greeting */}
              {guestName ? (
                <p className="text-lg sm:text-xl text-gray-700 mb-8 max-w-xl mx-auto">
                  Hi <span className="font-semibold">{guestName}</span>, we'd love to work with you.
                </p>
              ) : (
                <p className="text-lg sm:text-xl text-gray-700 mb-8 max-w-xl mx-auto">
                  <EditableText
                    sectionId={`invite-hero-${pilotType}`}
                    field="greeting"
                    defaultValue="We'd love to work with you."
                    className="inline"
                  />
                </p>
              )}
              
              {/* CTA Button - See How It Works navigates to demo tab */}
              <Button
                onClick={() => { setActiveTab("demo"); setActiveDemoTab(pilotType); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                size="lg"
                className={`bg-gradient-to-r ${config.color} hover:opacity-90 text-white h-12 px-8 rounded-full font-medium text-base shadow-lg`}
                data-testid="see-how-it-works-btn"
              >
                See How It Works
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </EditableSection>

          {/* Demo Content Section - Inline based on pilot type */}
          <PilotDemoContent pilotType={pilotType} config={config} settings={subCategorySettings} onAccept={() => setShowRsvpForm(true)} />

      {/* What This Means Section - Editable */}
      <EditableSection sectionId={`invite-benefits-${pilotType}`} label="What Being a Pilot Partner Means" className="py-16 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-center mb-4">
            <EditableText
              sectionId={`invite-benefits-${pilotType}`}
              field="heading"
              defaultValue="What being a pilot partner means"
              className="inline"
            />
          </h2>
          <p className="text-gray-600 text-center mb-10 max-w-xl mx-auto">
            <EditableText
              sectionId={`invite-benefits-${pilotType}`}
              field="subheading"
              defaultValue="You're not just a user. You're helping build the future of client experiences."
              className="inline"
            />
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Step 1 */}
            <Card className="border-gray-100 hover:shadow-md transition-shadow text-center">
              <CardContent className="p-6">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-pink-100 to-purple-100 flex items-center justify-center mx-auto mb-4">
                  <Zap className="w-6 h-6 text-purple-600" />
                </div>
                <h3 className="font-semibold mb-2">
                  <EditableText
                    sectionId={`invite-benefits-${pilotType}`}
                    field="step1_title"
                    defaultValue="Create your experience"
                    className="inline"
                  />
                </h3>
                <p className="text-sm text-gray-600">
                  <EditableText
                    sectionId={`invite-benefits-${pilotType}`}
                    field="step1_desc"
                    defaultValue="We'll build your personalized phygital experience based on how you work."
                    className="inline"
                  />
                </p>
              </CardContent>
            </Card>
            
            {/* Step 2 */}
            <Card className="border-gray-100 hover:shadow-md transition-shadow text-center">
              <CardContent className="p-6">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-100 to-cyan-100 flex items-center justify-center mx-auto mb-4">
                  <Eye className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="font-semibold mb-2">
                  <EditableText
                    sectionId={`invite-benefits-${pilotType}`}
                    field="step2_title"
                    defaultValue="Test it with us"
                    className="inline"
                  />
                </h3>
                <p className="text-sm text-gray-600">
                  <EditableText
                    sectionId={`invite-benefits-${pilotType}`}
                    field="step2_desc"
                    defaultValue="Try the platform, give feedback, and help us make it perfect for you."
                    className="inline"
                  />
                </p>
              </CardContent>
            </Card>
            
            {/* Step 3 */}
            <Card className="border-gray-100 hover:shadow-md transition-shadow text-center">
              <CardContent className="p-6">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-emerald-100 to-teal-100 flex items-center justify-center mx-auto mb-4">
                  <Gift className="w-6 h-6 text-emerald-600" />
                </div>
                <h3 className="font-semibold mb-2">
                  <EditableText
                    sectionId={`invite-benefits-${pilotType}`}
                    field="step3_title"
                    defaultValue="Free Legacy tier for life"
                    className="inline"
                  />
                </h3>
                <p className="text-sm text-gray-600">
                  <EditableText
                    sectionId={`invite-benefits-${pilotType}`}
                    field="step3_desc"
                    defaultValue="Pilot partners get lifetime access to phygital Legacy tier - our thank you."
                    className="inline"
                  />
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </EditableSection>

      {/* CTA Section - Editable */}
      <EditableSection sectionId={`invite-cta-${pilotType}`} label="Call to Action Section" className={`py-16 px-4 bg-gradient-to-r ${config.color} text-white`}>
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl font-bold mb-4">
            <EditableText
              sectionId={`invite-cta-${pilotType}`}
              field="heading"
              defaultValue="Ready to join?"
              className="inline"
            />
          </h2>
          <p className="text-white/80 mb-8">
            <EditableText
              sectionId={`invite-cta-${pilotType}`}
              field="subheading"
              defaultValue="It only takes a few minutes."
              className="inline"
            />
          </p>
          <Button
            onClick={() => setShowRsvpForm(true)}
            size="lg"
            className="bg-white text-gray-900 hover:bg-gray-100 h-12 px-8 rounded-full font-medium"
            data-testid="cta-join-btn"
          >
            <EditableText
              sectionId={`invite-cta-${pilotType}`}
              field="button_text"
              defaultValue="Accept Invite"
              className="inline"
            />
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </EditableSection>
        </>
      ) : (
        /* Full Demo Tab - Renders the EXACT landing page content for the selected category */
        <div className="demo-page-wrapper">
          {/* Render category-specific demo content */}
          {(() => {
            const demoType = activeDemoTab || pilotType;
            const acceptHandler = () => setShowRsvpForm(true);
            const portfolioImages = inviteData?.portfolio_images || [];
            
            // Map categories to their specific demo components
            switch(demoType) {
              case "makeup":
                return <MUADemoContent onAccept={acceptHandler} portfolioImages={portfolioImages} />;
              case "hair":
                return <HairDemoContent onAccept={acceptHandler} portfolioImages={portfolioImages} />;
              case "nail":
                return <NailDemoContent onAccept={acceptHandler} portfolioImages={portfolioImages} />;
              case "lash":
                return <LashDemoContent onAccept={acceptHandler} portfolioImages={portfolioImages} />;
              case "culinary":
                return <CulinaryDemoContent onAccept={acceptHandler} portfolioImages={portfolioImages} />;
              default:
                // Generic demo for categories without specific components yet (event, wellness, instructor)
                return (
                  <div className="min-h-screen bg-gray-50">
                    {/* Demo Header */}
                    <div className="bg-white border-b py-4 px-4 sticky top-[120px] z-40">
                      <div className="max-w-6xl mx-auto flex items-center justify-between">
                        <div>
                          <h2 className="text-lg font-bold flex items-center gap-2">
                            <Monitor className="w-5 h-5 text-purple-600" />
                            {(PILOT_CONFIGS[demoType] || config).title} Demo
                          </h2>
                          <p className="text-sm text-gray-500">
                            See everything phygital can do for {(PILOT_CONFIGS[demoType] || config).title.toLowerCase()} providers
                          </p>
                        </div>
                        <Button
                          onClick={acceptHandler}
                          className={`bg-gradient-to-r ${config.color} hover:opacity-90 text-white rounded-full`}
                        >
                          Accept Invite
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                      </div>
                    </div>
                    
                    {/* Full Demo Content - Renders based on active demo tab */}
                    <FullPilotDemo pilotType={demoType} config={PILOT_CONFIGS[demoType] || config} />
                    
                    {/* CTA Section */}
                    <section className={`py-16 px-4 bg-gradient-to-r ${config.color} text-white`}>
                      <div className="max-w-3xl mx-auto text-center">
                        <h2 className="text-2xl sm:text-3xl font-bold mb-4">
                          Ready to build your experience?
                        </h2>
                        <p className="text-white/80 mb-8">
                          It only takes a few minutes.
                        </p>
                        <Button
                          onClick={acceptHandler}
                          size="lg"
                          className="bg-white text-gray-900 hover:bg-gray-100 h-12 px-8 rounded-full font-medium"
                        >
                          Accept Invite
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                      </div>
                    </section>
                  </div>
                );
            }
          })()}
        </div>
      )}

      {/* Footer */}
      <footer className="py-8 px-4 bg-white border-t">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-xs text-gray-500">
            This is a private pilot program. Access is by invitation only.
            <br />
            © 2026 Phygital Systems. All rights reserved.
          </p>
        </div>
      </footer>

      {/* RSVP Form Modal */}
      <Dialog open={showRsvpForm} onOpenChange={setShowRsvpForm}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Let's get you set up</DialogTitle>
            <DialogDescription>Just a few details to get started.</DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            {/* Name */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="firstName">First name</Label>
                <Input
                  id="firstName"
                  value={formData.firstName}
                  onChange={(e) => setFormData(prev => ({ ...prev, firstName: e.target.value }))}
                  placeholder="First"
                  required
                  data-testid="input-first-name"
                />
              </div>
              <div>
                <Label htmlFor="lastName">Last name</Label>
                <Input
                  id="lastName"
                  value={formData.lastName}
                  onChange={(e) => setFormData(prev => ({ ...prev, lastName: e.target.value }))}
                  placeholder="Last"
                  required
                  data-testid="input-last-name"
                />
              </div>
            </div>
            
            {/* Email */}
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                placeholder="you@example.com"
                required
                data-testid="input-email"
              />
            </div>
            
            {/* Phone */}
            <div>
              <Label htmlFor="phone">Phone</Label>
              <Input
                id="phone"
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                placeholder="+1 (555) 123-4567"
                data-testid="input-phone"
              />
            </div>
            
            {/* Business Name */}
            <div>
              <Label htmlFor="businessName">Business / Studio name</Label>
              <Input
                id="businessName"
                value={formData.businessName}
                onChange={(e) => setFormData(prev => ({ ...prev, businessName: e.target.value }))}
                placeholder="Your business name"
                data-testid="input-business"
              />
            </div>
            
            {/* City */}
            <div>
              <Label htmlFor="city">City</Label>
              <LocationInput
                value={formData.city}
                onChange={(value) => setFormData(prev => ({ ...prev, city: value }))}
                placeholder="Search your city"
              />
            </div>
            
            {/* Pilot Categories */}
            <div>
              <Label className="mb-2 block">What services do you offer?</Label>
              <div className="flex flex-wrap gap-2">
                {PILOT_CATEGORIES.map((cat) => (
                  <button
                    key={cat.id}
                    type="button"
                    onClick={() => toggleCategory(cat.id)}
                    className={`px-3 py-1.5 rounded-full text-sm flex items-center gap-1.5 transition-colors ${
                      formData.pilotCategories.includes(cat.id)
                        ? "bg-purple-600 text-white"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                    data-testid={`category-${cat.id}`}
                  >
                    <cat.icon className="w-3.5 h-3.5" />
                    {cat.label}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Solo or Team */}
            <div>
              <Label className="mb-2 block">Do you work solo or with a team?</Label>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, isSolo: true }))}
                  className={`flex-1 py-2 px-4 rounded-lg border text-sm font-medium transition-colors ${
                    formData.isSolo
                      ? "border-purple-600 bg-purple-50 text-purple-700"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                  data-testid="btn-solo"
                >
                  Solo
                </button>
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, isSolo: false }))}
                  className={`flex-1 py-2 px-4 rounded-lg border text-sm font-medium transition-colors ${
                    !formData.isSolo
                      ? "border-purple-600 bg-purple-50 text-purple-700"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                  data-testid="btn-team"
                >
                  Team
                </button>
              </div>
            </div>
            
            {/* Team Services (if team selected) - Multi-select same as services */}
            {!formData.isSolo && (
              <div>
                <Label className="mb-2 block">What services does your team provide?</Label>
                <p className="text-xs text-gray-500 mb-2">Select all that apply</p>
                <div className="flex flex-wrap gap-2">
                  {PILOT_CATEGORIES.map((cat) => (
                    <button
                      key={cat.id}
                      type="button"
                      onClick={() => {
                        setFormData(prev => ({
                          ...prev,
                          teamCategories: prev.teamCategories.includes(cat.id)
                            ? prev.teamCategories.filter(c => c !== cat.id)
                            : [...prev.teamCategories, cat.id]
                        }));
                      }}
                      className={`px-3 py-1.5 rounded-full text-sm flex items-center gap-1.5 transition-colors ${
                        formData.teamCategories.includes(cat.id)
                          ? "bg-purple-600 text-white"
                          : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                      }`}
                      data-testid={`team-category-${cat.id}`}
                    >
                      <cat.icon className="w-3.5 h-3.5" />
                      {cat.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
            
            {/* Error */}
            {error && (
              <p className="text-sm text-red-600">{error}</p>
            )}
            
            {/* Submit */}
            <Button
              type="submit"
              className={`w-full bg-gradient-to-r ${config.color} hover:opacity-90 text-white h-11 rounded-full`}
              disabled={submitting}
              data-testid="submit-rsvp"
            >
              {submitting ? "Setting up..." : "Continue"}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </form>
        </DialogContent>
      </Dialog>
      </div>{/* End of main content wrapper that fades in after envelope */}
    </div>
  );
}

// ============================================================================
// PILOT DEMO CONTENT - Matches phygital.systems landing pages exactly
// ============================================================================
function PilotDemoContent({ pilotType, config, settings, onAccept }) {
  const s = settings || {};
  
  // CTA button handler - use onAccept if provided, otherwise do nothing for demo purposes
  const handleCTA = () => {
    if (onAccept) onAccept();
  };

  // ==========================================================================
  // NAIL DEMO - Matches phygital.systems/nails
  // ==========================================================================
  if (pilotType === "nail") {
    return (
      <section className="py-12 px-4 bg-white">
        <div className="max-w-5xl mx-auto">
          
          {/* Hero */}
          <div className="text-center mb-16">
            <p className="text-pink-600 text-sm font-medium mb-2">Nail Pilot by Phygital</p>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              {s.demo_hero_title || "See Their Vision"}
            </h2>
            <p className="text-2xl sm:text-3xl font-bold text-gray-400 mb-6">Before They Sit Down</p>
            <p className="text-gray-600 max-w-xl mx-auto mb-6">
              {s.demo_hero_subtitle || "Design, Inspo, and Prep Details  -  Each client shares their vision before arriving. You see the supplies and time needed."}
            </p>
            <div className="flex flex-wrap justify-center gap-2">
              {["Nail Salons", "Independent Techs", "Works with any booker"].map(tag => (
                <Badge key={tag} variant="secondary" className="bg-pink-50 text-pink-700">{tag}</Badge>
              ))}
            </div>
          </div>

          {/* What You See vs Client View */}
          <div className="grid md:grid-cols-2 gap-6 mb-16">
            {/* What You See */}
            <div className="bg-gray-50 rounded-2xl p-6">
              <p className="text-xs font-medium text-gray-500 mb-4">WHAT YOU SEE</p>
              <div className="bg-white rounded-xl p-4 border">
                <p className="text-sm font-medium mb-1">Today's Appointments</p>
                <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <p className="font-medium">Maya</p>
                      <p className="text-sm text-gray-500">Gel Mani</p>
                    </div>
                    <p className="text-xs text-gray-400">2:00 PM</p>
                  </div>
                  <p className="text-sm text-gray-600 mb-2">Design: Nude base with gold chrome French tips</p>
                  <p className="text-xs text-pink-600">Noted: Sensitive cuticles</p>
                </div>
              </div>
            </div>
            {/* Client View */}
            <div className="bg-gray-50 rounded-2xl p-6">
              <p className="text-xs font-medium text-gray-500 mb-4">CLIENT VIEW</p>
              <div className="bg-white rounded-xl p-4 border">
                <p className="text-sm text-gray-600 mb-3">Share your nail vision for your appointment</p>
                <div className="space-y-3">
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Shape</p>
                    <div className="flex gap-2">
                      {["Almond", "Coffin", "Square", "Oval"].map(shape => (
                        <span key={shape} className={`px-2 py-1 rounded text-xs ${shape === "Almond" ? "bg-pink-100 text-pink-700" : "bg-gray-100"}`}>{shape}</span>
                      ))}
                    </div>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Length</p>
                    <div className="flex gap-2">
                      {["Short", "Medium", "Long"].map(len => (
                        <span key={len} className={`px-2 py-1 rounded text-xs ${len === "Medium" ? "bg-pink-100 text-pink-700" : "bg-gray-100"}`}>{len}</span>
                      ))}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {["Matte", "Chrome", "Glitter", "French"].map(style => (
                      <span key={style} className="px-2 py-1 rounded text-xs bg-gray-100">{style}</span>
                    ))}
                  </div>
                </div>
                <p className="text-xs text-green-600 mt-3">✓ Preferences saved</p>
              </div>
            </div>
          </div>

          {/* Built for Nail Pros */}
          <div className="mb-16">
            <h3 className="text-2xl font-bold text-center mb-2">Built for Nail Pros</h3>
            <p className="text-gray-500 text-center mb-8">Salons, independent techs, mobile or studio. Phygital helps you prep better and serve faster.</p>
            
            <div className="grid md:grid-cols-3 gap-6">
              {[
                { title: "Inspo Before They Arrive", desc: "Clients share Pinterest boards, photos, and design ideas when they book. Not when they sit down.", icon: Image },
                { title: "Accurate Time Estimates", desc: "Know if it's a simple color or intricate nail art before scheduling. No more rushed appointments.", icon: Clock },
                { title: "Color & Product Prep", desc: "See their preferred shades and know what to pull before they walk in.", icon: Palette },
                { title: "Allergy & Sensitivity Alerts", desc: "Know about sensitivities to acetone, gel allergies, or cuticle sensitivity ahead of time.", icon: Bell },
                { title: "Remember Every Client", desc: "Shape preference, length, favorite colors. All saved and ready for repeat visits.", icon: Heart },
                { title: "Accurate Pricing Upfront", desc: "Quote correctly based on design complexity. No awkward price surprises at checkout.", icon: DollarSign }
              ].map((feature) => (
                <div key={feature.title} className="bg-gray-50 rounded-xl p-5">
                  <div className="w-10 h-10 rounded-full bg-pink-100 flex items-center justify-center mb-3">
                    <feature.icon className="w-5 h-5 text-pink-600" />
                  </div>
                  <h4 className="font-bold mb-2">{feature.title}</h4>
                  <p className="text-sm text-gray-600">{feature.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Image Inspo Gallery - Nail designs showcase */}
          <div className="bg-gradient-to-br from-pink-50 to-rose-50 rounded-2xl p-6 shadow-sm border mb-16">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-full bg-pink-100 flex items-center justify-center">
                <Camera className="w-4 h-4 text-pink-600" />
              </div>
              <h3 className="text-lg font-bold">Image Inspo Gallery</h3>
            </div>
            <p className="text-gray-500 text-sm mb-6">
              Showcase your best nail designs for clients to browse and get inspired
            </p>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[1, 2, 3, 4].map((i) => (
                <EditableImage
                  key={i}
                  sectionId={`pilot-inspo-gallery-nail`}
                  field={`gallery_image_${i}`}
                  defaultImage={null}
                  className="aspect-square rounded-xl overflow-hidden shadow-sm"
                  placeholderClassName="bg-gradient-to-br from-pink-100 to-rose-100"
                  placeholderIcon={<Camera className="w-6 h-6 text-pink-400" />}
                  alt={`Nail design ${i}`}
                />
              ))}
            </div>
            <p className="text-center text-xs text-gray-400 mt-4">Click images to upload your portfolio designs</p>
          </div>

          {/* Works With All Your Services */}
          <div className="mb-16">
            <h3 className="text-xl font-bold text-center mb-8">Works With All Your Services</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { name: "Classic Mani", price: "$35+" },
                { name: "Gel Manicure", price: "$55+" },
                { name: "Full Set Acrylics", price: "$85+" },
                { name: "Nail Art", price: "$15+" }
              ].map((service) => (
                <div key={service.name} className="bg-white rounded-xl p-4 border text-center">
                  <p className="font-medium">{service.name}</p>
                  <p className="text-pink-600 text-sm">{service.price}</p>
                </div>
              ))}
            </div>
            <p className="text-center text-sm text-gray-500 mt-4">Free during pilot. No booking system changes required.</p>
          </div>

          {/* CTA */}
          <div className="bg-gradient-to-r from-pink-600 to-rose-600 rounded-2xl p-8 text-center text-white">
            <h3 className="text-xl font-bold mb-2">Ready to See Client Visions Before They Arrive?</h3>
            <p className="text-white/80 mb-4">Join the pilot program and get early access.</p>
            <Button className="bg-white text-pink-600 hover:bg-white/90" onClick={handleCTA}>
              Accept Invite <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </section>
    );
  }

  // Makeup Demo Content - Matches phygital.systems/mua landing page
  if (pilotType === "makeup") {
    const s = settings || {};
    
    return (
      <section className="py-12 px-4 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          
          {/* Intro - Works with Acuity */}
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold mb-3">
              {s.demo_section_title || "Know Your Client's Look Before They Sit Down"}
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Keep using Acuity for booking. Phygital adds a preference layer so clients share their look, inspiration, and vibe before every appointment.
            </p>
            <div className="flex flex-wrap justify-center gap-2 mt-6">
              {["Works with Acuity", "Inspo before booking", "Change notifications", "Look approval flow"].map(tag => (
                <Badge key={tag} variant="secondary" className="bg-purple-100 text-purple-700">
                  {tag}
                </Badge>
              ))}
            </div>
          </div>

          {/* Feature 1: Style, Supplies, and Pre-Prep */}
          <div className="bg-white rounded-2xl p-6 shadow-sm border mb-8">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center">
                <ClipboardList className="w-4 h-4 text-purple-600" />
              </div>
              <h3 className="text-lg font-bold">{s.demo_card1_title || "Style, Supplies, and Pre-Prep"}</h3>
            </div>
            <p className="text-gray-500 text-sm mb-4">
              {s.demo_card1_subtitle || "Each client selects a look from your portfolio. You see the supplies and prep needed."}
            </p>
            
            <div className="grid md:grid-cols-2 gap-4">
              {/* Client View */}
              <div className="bg-gray-50 rounded-xl p-4">
                <p className="text-xs text-gray-500 mb-2 font-medium">CLIENT VIEW</p>
                <div className="bg-white rounded-lg p-3 border">
                  <p className="text-sm font-medium mb-2">Select your look</p>
                  <p className="text-xs text-gray-400 mb-3">from MUA portfolio</p>
                  <div className="flex items-center gap-2 p-2 bg-purple-50 rounded-lg mb-2">
                    <Check className="w-4 h-4 text-purple-600" />
                    <span className="text-sm font-medium">Soft Glam selected</span>
                  </div>
                  <Button variant="outline" size="sm" className="w-full text-xs">
                    + Add your own inspo
                  </Button>
                </div>
              </div>
              
              {/* MUA View */}
              <div className="bg-gray-50 rounded-xl p-4">
                <p className="text-xs text-gray-500 mb-2 font-medium">YOUR VIEW</p>
                <div className="bg-white rounded-lg p-3 border">
                  <p className="text-sm font-medium mb-1">Today's Appointments</p>
                  <div className="p-2 bg-green-50 rounded-lg mb-3">
                    <p className="text-sm font-medium">Jordan - 9:00 AM</p>
                    <p className="text-xs text-gray-500">Soft Glam</p>
                  </div>
                  <p className="text-xs font-medium text-gray-600 mb-1">Supplies Needed:</p>
                  <div className="flex flex-wrap gap-1 mb-2">
                    {["Foundation", "Lashes", "Lip Liner", "Setting Spray"].map(item => (
                      <Badge key={item} variant="outline" className="text-xs">{item}</Badge>
                    ))}
                  </div>
                  <p className="text-xs text-green-600">✓ Ready</p>
                </div>
              </div>
            </div>
          </div>

          {/* Feature 2: Look Inspiration with editable images */}
          <div className="bg-white rounded-2xl p-6 shadow-sm border mb-8">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-full bg-pink-100 flex items-center justify-center">
                <Image className="w-4 h-4 text-pink-600" />
              </div>
              <h3 className="text-lg font-bold">{s.demo_card2_title || "How Clients Share Their Look"}</h3>
            </div>
            <p className="text-gray-500 text-sm mb-4">
              {s.demo_card2_subtitle || "After booking on Acuity, clients get a link to share their preferences"}
            </p>
            
            <div className="bg-gray-50 rounded-xl p-4">
              <div className="bg-white rounded-lg p-4 border max-w-sm mx-auto">
                <p className="text-sm font-medium mb-1">Share Your Look</p>
                <p className="text-xs text-gray-400 mb-4">Pick from the MUA's portfolio or add your own inspo</p>
                
                <div className="mb-4">
                  <p className="text-xs text-gray-500 mb-2">Select a look</p>
                  <div className="p-2 bg-purple-50 rounded-lg flex items-center gap-2">
                    <Check className="w-4 h-4 text-purple-600" />
                    <span className="text-sm">Soft Glam</span>
                  </div>
                </div>
                
                <p className="text-xs text-gray-500 mb-2">Inspiration images</p>
                <div className="grid grid-cols-4 gap-2 mb-4">
                  {[1, 2, 3, 4].map((i) => (
                    <EditableImage
                      key={i}
                      sectionId={`pilot-demo-inspo-${pilotType}`}
                      field={`inspo_image_${i}`}
                      defaultImage={null}
                      className="aspect-square rounded-lg overflow-hidden"
                      placeholderClassName="bg-gradient-to-br from-purple-200 to-pink-200"
                      placeholderIcon={<Camera className="w-4 h-4 text-purple-400" />}
                      alt={`Inspiration ${i}`}
                    />
                  ))}
                </div>
                
                <Button className="w-full bg-purple-600 hover:bg-purple-700" size="sm">
                  Send to MUA
                </Button>
              </div>
            </div>
          </div>

          {/* Feature 3: Track What You Need */}
          <div className="bg-white rounded-2xl p-6 shadow-sm border mb-8">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle2 className="w-4 h-4 text-green-600" />
              </div>
              <h3 className="text-lg font-bold">{s.demo_card3_title || "Track What You Need"}</h3>
            </div>
            <p className="text-gray-500 text-sm mb-4">
              {s.demo_card3_subtitle || "See supplies needed for each booking and track directly in the app"}
            </p>
            
            <div className="bg-gray-50 rounded-xl p-4">
              <div className="bg-white rounded-lg p-4 border max-w-md mx-auto">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm font-medium">Prep Tracker</p>
                  <Badge className="bg-green-100 text-green-700">Confirmed</Badge>
                </div>
                <p className="text-xs text-gray-500 mb-3">Upcoming: Jordan - Jan 25</p>
                
                <p className="text-xs font-medium text-gray-600 mb-2">Supplies Checklist</p>
                <div className="space-y-2">
                  {[
                    "Foundation - Medium coverage, dewy",
                    "Berry lip color (client request)",
                    "Natural mink lashes",
                    "Setting spray"
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm">
                      <div className="w-4 h-4 rounded border border-gray-300" />
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
                
                <div className="mt-3 p-2 bg-amber-50 rounded-lg">
                  <p className="text-xs text-amber-700">
                    <strong>Client note:</strong> "Want to try a berry lip color"
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Feature 4: Change Notifications */}
          <div className="bg-gradient-to-r from-purple-600 to-indigo-600 rounded-2xl p-6 text-white mb-8">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                <Bell className="w-4 h-4 text-white" />
              </div>
              <h3 className="text-lg font-bold">Client Changes? You'll Know</h3>
            </div>
            <p className="text-white/80 text-sm mb-4">
              If a client updates their look or adds new inspo, you get notified instantly.
            </p>
            
            <div className="bg-white/10 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 rounded-full bg-amber-400 mt-2" />
                <div>
                  <p className="text-sm">Client updated their look <span className="text-white/60">2 min ago</span></p>
                  <p className="text-xs text-white/70 mt-1">Changed from "bold lip" to "natural nude lip". Added 1 new inspo</p>
                  <div className="flex gap-2 mt-3">
                    <Button size="sm" variant="secondary" className="text-xs bg-white text-purple-700">
                      ✓ Approve Changes
                    </Button>
                    <Button size="sm" variant="outline" className="text-xs border-white/30 text-white hover:bg-white/10">
                      Ask Question
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Workflow Steps */}
          <div className="bg-white rounded-2xl p-6 shadow-sm border">
            <h3 className="text-lg font-bold text-center mb-2">Your Acuity booking stays exactly the same</h3>
            <p className="text-gray-500 text-sm text-center mb-6">Phygital adds a preference layer after they book</p>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { num: "1", title: "Book on Acuity", desc: "Client selects service & time" },
                { num: "2", title: "Phygital Prompt", desc: "Share preferences for this appointment" },
                { num: "3", title: "MUA Reviews", desc: "See look, inspo, and confirm" },
                { num: "4", title: "Day Of", desc: "Everything ready, no guesswork" }
              ].map(step => (
                <div key={step.num} className="text-center">
                  <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center mx-auto mb-2">
                    <span className="text-purple-600 font-bold">{step.num}</span>
                  </div>
                  <p className="font-medium text-sm">{step.title}</p>
                  <p className="text-xs text-gray-500">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
          
        </div>
      </section>
    );
  }

  // ==========================================================================
  // HAIR DEMO - Matches phygital.systems/hair exactly
  // ==========================================================================
  if (pilotType === "hair") {
    return (
      <section className="py-12 px-4 bg-white">
        <div className="max-w-5xl mx-auto">
          
          {/* Hero */}
          <div className="text-center mb-16">
            <p className="text-purple-600 text-sm font-medium mb-2">Hair Pilot by Phygital</p>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              {s.demo_hero_title || "Styles Booked Seamlessly"}
            </h2>
            <p className="text-gray-600 max-w-xl mx-auto mb-6">
              {s.demo_hero_subtitle || "Clients find you on IG, DM to book. You send them a link, they fill their preferences, you confirm the price. Address released only after they confirm."}
            </p>
            <div className="flex flex-wrap justify-center gap-2">
              {["IG to Booked", "iCloud sync", "Address after confirm", "Story generator"].map(tag => (
                <Badge key={tag} variant="secondary" className="bg-purple-50 text-purple-700">{tag}</Badge>
              ))}
            </div>
          </div>

          {/* Your Booking Flow - From DM to Done */}
          <div className="bg-gray-50 rounded-2xl p-8 mb-16">
            <h3 className="text-xl font-bold text-center mb-2">Your Booking Flow</h3>
            <p className="text-2xl font-bold text-center text-purple-600 mb-2">From DM to Done</p>
            <p className="text-gray-500 text-center mb-8">No booking platform needed. Clients text or DM you, you handle the rest.</p>
            
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
              {[
                { num: "1", title: "Client DMs You", desc: "Find you on IG, text or DM to book", icon: MessageSquare },
                { num: "2", title: "Send Preference Link", desc: "Create booking, send phygital link", icon: Share },
                { num: "3", title: "Client Fills Preferences", desc: "Style, length, vibe - all captured", icon: Check },
                { num: "4", title: "You Confirm & Price", desc: "Review, set final price in app", icon: Star },
                { num: "5", title: "Reminder Sent", desc: "Day before: client confirms", icon: Calendar },
                { num: "6", title: "Address Released", desc: "Location sent after confirmation", icon: Heart }
              ].map((step) => (
                <div key={step.num} className="text-center">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-pink-100 to-purple-100 flex items-center justify-center mx-auto mb-2">
                    <step.icon className="w-5 h-5 text-purple-600" />
                  </div>
                  <p className="font-medium text-sm">{step.title}</p>
                  <p className="text-xs text-gray-500 mt-1">{step.desc}</p>
                </div>
              ))}
            </div>

            {/* Example DM Conversation */}
            <div className="max-w-sm mx-auto bg-white rounded-xl p-4 border">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-pink-400 to-purple-400" />
                <p className="font-medium text-sm">Maya</p>
              </div>
              <div className="bg-gray-100 rounded-lg p-3 text-sm">
                Hey! I saw your knotless braids and I'm obsessed. Do you have any openings this weekend?
              </div>
              <div className="flex gap-2 mt-3">
                {["Text", "DM", "iMessage"].map(opt => (
                  <span key={opt} className="px-2 py-1 rounded text-xs bg-gray-100">{opt}</span>
                ))}
              </div>
            </div>
          </div>

          {/* Select a Style */}
          <div className="mb-16">
            <h3 className="text-xl font-bold text-center mb-2">Select a Style</h3>
            <p className="text-gray-500 text-center mb-2">Browse styles, see preset looks, customize your options, and get what you need to bring.</p>
            <p className="text-purple-600 text-sm text-center mb-6">Show prices to clients? <span className="font-medium">Yes</span></p>
            
            {/* Style Tabs */}
            <div className="flex flex-wrap justify-center gap-2 mb-6">
              {["Knotless Braids", "Corn Rows", "Lemonade Braids", "Tribal Braids", "Quick Weaves"].map((style, idx) => (
                <span key={style} className={`px-4 py-2 rounded-full text-sm ${idx === 0 ? "bg-purple-600 text-white" : "bg-gray-100 text-gray-700"}`}>
                  {style}
                </span>
              ))}
            </div>

            {/* Preset Looks - Editable Images */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
              {[
                { name: "Classic Medium", price: "$180+", popular: true, id: 1 },
                { name: "Boho Style", price: "$220+", popular: true, id: 2 },
                { name: "Jumbo", price: "$150+", id: 3 },
                { name: "Bob Length", price: "$140+", id: 4 },
                { name: "Add Inspo", price: "", isAdd: true, id: 5 }
              ].map((style) => (
                <div key={style.name} className={`bg-white rounded-xl p-4 border ${style.isAdd ? "border-dashed border-purple-300" : ""}`}>
                  {style.popular && <span className="text-[10px] bg-pink-100 text-pink-600 px-2 py-0.5 rounded-full">Popular</span>}
                  {style.isAdd ? (
                    <div className="w-full h-20 rounded-lg mb-2 flex items-center justify-center bg-purple-50">
                      <Plus className="w-6 h-6 text-purple-400" />
                    </div>
                  ) : (
                    <EditableImage
                      sectionId={`pilot-demo-styles-${pilotType}`}
                      field={`style_image_${style.id}`}
                      defaultImage={null}
                      className="w-full h-20 rounded-lg mb-2"
                      placeholderClassName="bg-gradient-to-br from-pink-200 to-purple-200 flex items-center justify-center"
                      placeholderIcon={<Instagram className="w-6 h-6 text-purple-400" />}
                      alt={style.name}
                    />
                  )}
                  <p className="font-medium text-sm">{style.name}</p>
                  {style.price && <p className="text-xs text-purple-600">{style.price}</p>}
                </div>
              ))}
            </div>

            {/* Customization Options */}
            <div className="bg-gray-50 rounded-xl p-6">
              <p className="font-medium mb-4">Customize</p>
              <div className="grid md:grid-cols-4 gap-6">
                <div>
                  <p className="text-xs text-gray-500 mb-2">Length</p>
                  <div className="space-y-1">
                    {["Bob/Shoulder", "Mid Back +$30", "Lower Back +$60", "Below Butt +$100"].map((opt, i) => (
                      <div key={opt} className={`px-3 py-1.5 rounded text-xs ${i === 0 ? "bg-purple-100 text-purple-700" : "bg-white"}`}>{opt}</div>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-2">Style</p>
                  <div className="space-y-1">
                    {["Classic", "Boho Style +$40"].map((opt, i) => (
                      <div key={opt} className={`px-3 py-1.5 rounded text-xs ${i === 0 ? "bg-purple-100 text-purple-700" : "bg-white"}`}>{opt}</div>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-2">Size</p>
                  <div className="space-y-1">
                    {["Medium", "Large"].map((opt, i) => (
                      <div key={opt} className={`px-3 py-1.5 rounded text-xs ${i === 0 ? "bg-purple-100 text-purple-700" : "bg-white"}`}>{opt}</div>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-2">Parts</p>
                  <div className="space-y-1">
                    {["Standard", "Extra Large Parts +$25"].map((opt, i) => (
                      <div key={opt} className={`px-3 py-1.5 rounded text-xs ${i === 0 ? "bg-purple-100 text-purple-700" : "bg-white"}`}>{opt}</div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* What You Need to Bring */}
          <div className="bg-white rounded-2xl p-6 border mb-16">
            <h3 className="text-lg font-bold mb-4">What You Need to Bring</h3>
            <div className="grid md:grid-cols-3 gap-6">
              <div>
                <p className="text-xs text-gray-500 mb-1">Hair Type</p>
                <p className="font-medium">Pre-stretched braiding hair</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">Amount</p>
                <p className="font-medium">7-9 packs</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">Recommended Brands</p>
                <div className="flex flex-wrap gap-2 mt-1">
                  {["x-pression", "Outre", "Freetress", "Amazon"].map(brand => (
                    <span key={brand} className="px-2 py-1 rounded text-xs bg-gray-100">{brand}</span>
                  ))}
                </div>
              </div>
            </div>
            
            <div className="grid md:grid-cols-2 gap-4 mt-6">
              <div className="bg-purple-50 rounded-xl p-4">
                <p className="font-medium text-sm">Provide It For Me</p>
                <p className="text-xs text-gray-600">Stylist will have quality hair ready for you</p>
                <p className="text-purple-600 font-medium mt-2">+$50</p>
              </div>
              <div className="bg-amber-50 rounded-xl p-4">
                <p className="font-medium text-sm">Can't Wash Beforehand?</p>
                <p className="text-xs text-gray-600">We offer wash service</p>
                <p className="text-amber-600 font-medium mt-2">+$50</p>
              </div>
            </div>
          </div>

          {/* Before Your Appointment */}
          <div className="bg-gray-50 rounded-2xl p-6 mb-16">
            <h3 className="text-lg font-bold mb-4">Before Your Appointment</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-white rounded-lg">
                <span className="text-sm">Come with hair freshly washed & dried</span>
                <Badge variant="destructive" className="bg-red-100 text-red-700">Required</Badge>
              </div>
              <div className="flex items-center justify-between p-3 bg-white rounded-lg">
                <span className="text-sm">No product in hair</span>
                <Badge variant="destructive" className="bg-red-100 text-red-700">Required</Badge>
              </div>
              <div className="flex items-center p-3 bg-white rounded-lg">
                <span className="text-sm text-gray-600">Detangled and blow-dried preferred</span>
              </div>
            </div>
          </div>

          {/* Know Exactly What They Want */}
          <div className="mb-16">
            <h3 className="text-xl font-bold text-center mb-8">Know Exactly What They Want</h3>
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <p className="font-medium mb-3">Style Preferences</p>
                <div className="space-y-2">
                  <div><span className="text-gray-500 text-sm">Edges:</span> <span className="text-sm">No edges / Dramatic edges</span></div>
                  <div><span className="text-gray-500 text-sm">Length:</span> <span className="text-sm">Shoulder • Mid back • Lower back • Below the butt</span></div>
                  <div><span className="text-gray-500 text-sm">Ends:</span> <span className="text-sm">No curls / Curls at the end</span></div>
                  <div><span className="text-gray-500 text-sm">Shade:</span> <span className="text-sm">1 (Jet Black) • 1B (Off Black) • 2 (Darkest Brown) • 4 • 27 • 30 • 33</span></div>
                </div>
              </div>
              <div>
                <p className="font-medium mb-3">Vibe Preferences</p>
                <div className="flex flex-wrap gap-2">
                  {[
                    { vibe: "Chat away", desc: "Love conversation" },
                    { vibe: "Little chat", desc: "Some conversation" },
                    { vibe: "Quiet please", desc: "Prefer silence" },
                    { vibe: "Tired (sleep ok)", desc: "May doze off" }
                  ].map(item => (
                    <span key={item.vibe} className="px-3 py-2 rounded-lg text-xs bg-purple-50 text-purple-700">
                      {item.vibe}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Share Your Menu on IG Stories */}
          <div className="bg-gradient-to-r from-purple-600 to-pink-600 rounded-2xl p-8 text-white mb-16">
            <h3 className="text-xl font-bold mb-2">Share Your Menu on IG Stories</h3>
            <p className="text-white/80 mb-6">Generate a beautiful story image with your services. Toggle prices, add promos.</p>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <div className="flex gap-2 mb-4">
                  <Badge className="bg-white/20">$Show Prices</Badge>
                </div>
                <div className="space-y-2 mb-4">
                  {["Knotless Braids", "Tribal Braids", "Corn Rows", "Lemonade Braids", "Quick Weaves"].map(service => (
                    <div key={service} className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded border border-white/30 bg-white/20" />
                      <span className="text-sm">{service}</span>
                    </div>
                  ))}
                </div>
                <Button className="bg-white text-purple-600 hover:bg-white/90">
                  Download Story Image
                </Button>
                <p className="text-xs text-white/60 mt-2">1080x1920px Ready for IG Stories</p>
              </div>
              <div className="bg-white/10 rounded-xl p-4">
                <p className="text-sm font-medium mb-2">Current Services</p>
                <div className="space-y-1 text-sm">
                  <p>+Knotless Braids $180+</p>
                  <p>Corn Rows $80+</p>
                  <p>Lemonade Braids $200</p>
                  <p>Tribal Braids $220+</p>
                </div>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="text-center">
            <h3 className="text-2xl font-bold mb-4">Ready to Streamline Your Bookings?</h3>
            <p className="text-gray-600 mb-6">Join the pilot. DM to booked, seamlessly.</p>
            <Button className="bg-purple-600 hover:bg-purple-700" onClick={handleCTA}>
              Accept Invite <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </section>
    );
  }

  // Wellness Demo Content - Using PILOT_CONFIGS data
  if (pilotType === "wellness") {
    const s = settings || {};
    const wellnessConfig = PILOT_CONFIGS.wellness;
    
    return (
      <section className="py-12 px-4 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          
          {/* Intro */}
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold mb-3">
              {s.demo_section_title || wellnessConfig.description}
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Keep using your booking platform. Phygital adds a preference layer so clients share their health info, pressure preferences, and comfort needs before every session.
            </p>
            <div className="flex flex-wrap justify-center gap-2 mt-6">
              {["Works with Acuity", "Health intake", "Pressure prefs", "Aromatherapy choices"].map(tag => (
                <Badge key={tag} variant="secondary" className="bg-emerald-100 text-emerald-700">
                  {tag}
                </Badge>
              ))}
            </div>
          </div>

          {/* Feature 1: Health & Comfort Preferences */}
          <div className="bg-white rounded-2xl p-6 shadow-sm border mb-8">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center">
                <Heart className="w-4 h-4 text-emerald-600" />
              </div>
              <h3 className="text-lg font-bold">{s.demo_card1_title || "Health & Comfort Preferences"}</h3>
            </div>
            <p className="text-gray-500 text-sm mb-4">
              {s.demo_card1_subtitle || "Understand client needs before they arrive"}
            </p>
            
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-gray-50 rounded-xl p-4">
                <p className="text-xs text-gray-500 mb-2 font-medium">CLIENT SHARES</p>
                <div className="space-y-2">
                  {wellnessConfig.clientPreferences.map((pref) => (
                    <div key={pref} className="flex items-center gap-2 text-sm">
                      <Check className="w-4 h-4 text-emerald-500" />
                      <span>{pref}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="bg-gray-50 rounded-xl p-4">
                <p className="text-xs text-gray-500 mb-2 font-medium">YOU SEE</p>
                <div className="bg-white rounded-lg p-3 border">
                  <p className="text-sm font-medium mb-1">Alex T. - 2:00 PM</p>
                  <p className="text-xs text-gray-500 mb-2">{wellnessConfig.services[1]?.name || "Deep Tissue"}</p>
                  <div className="flex flex-wrap gap-1">
                    {["Medium pressure", "Focus: shoulders", "Lavender scent"].map(tag => (
                      <Badge key={tag} variant="outline" className="text-xs">{tag}</Badge>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Feature 2: Service Menu */}
          <div className="bg-white rounded-2xl p-6 shadow-sm border mb-8">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-full bg-teal-100 flex items-center justify-center">
                <ClipboardList className="w-4 h-4 text-teal-600" />
              </div>
              <h3 className="text-lg font-bold">{s.demo_card2_title || "Your Service Menu"}</h3>
            </div>
            <p className="text-gray-500 text-sm mb-4">
              {s.demo_card2_subtitle || "Clients browse your services before booking"}
            </p>
            
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
              {wellnessConfig.services.map((service) => (
                <div key={service.name} className="bg-gray-50 rounded-xl p-3 border">
                  <p className="font-medium text-sm">{service.name}</p>
                  <p className="text-xs text-emerald-600">{service.price}</p>
                </div>
              ))}
            </div>
            
            <div>
              <p className="font-medium text-sm mb-2">Available Add-ons</p>
              <div className="flex flex-wrap gap-2">
                {wellnessConfig.addOns.map((addon) => (
                  <span key={addon} className="px-3 py-1.5 rounded-full text-xs bg-emerald-50 text-emerald-700">+ {addon}</span>
                ))}
              </div>
            </div>
          </div>

          {/* Feature 3: Session History */}
          <div className="bg-white rounded-2xl p-6 shadow-sm border mb-8">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center">
                <Clock className="w-4 h-4 text-purple-600" />
              </div>
              <h3 className="text-lg font-bold">{s.demo_card3_title || "Session History"}</h3>
            </div>
            <p className="text-gray-500 text-sm mb-4">
              {s.demo_card3_subtitle || "Remember what worked for each client"}
            </p>
            
            <div className="bg-gray-50 rounded-xl p-4 max-w-md mx-auto">
              <p className="text-xs font-medium text-gray-600 mb-2">Last 3 Sessions</p>
              <div className="space-y-2">
                {[
                  { date: "Jan 20", service: "Deep Tissue", note: "Loved it!" },
                  { date: "Dec 15", service: "Swedish", note: "Too light" },
                  { date: "Nov 10", service: "Hot Stone", note: "Perfect" }
                ].map(visit => (
                  <div key={visit.date} className="flex items-center justify-between p-2 bg-white rounded border text-sm">
                    <span className="text-gray-500">{visit.date}</span>
                    <span className="font-medium text-xs">{visit.service}</span>
                    <span className="text-emerald-600 text-xs">{visit.note}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Change Notifications */}
          <div className="bg-gradient-to-r from-emerald-600 to-teal-600 rounded-2xl p-6 text-white mb-8">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                <Bell className="w-4 h-4 text-white" />
              </div>
              <h3 className="text-lg font-bold">Client Updates? You'll Know</h3>
            </div>
            <p className="text-white/80 text-sm mb-4">
              If they have a new injury or change their preferences, you get notified.
            </p>
            <div className="bg-white/10 rounded-xl p-3">
              <p className="text-sm">⚠️ Alex updated: "New shoulder tension from desk work"</p>
            </div>
          </div>

          {/* Workflow */}
          <div className="bg-white rounded-2xl p-6 shadow-sm border">
            <h3 className="text-lg font-bold text-center mb-2">Your booking stays exactly the same</h3>
            <p className="text-gray-500 text-sm text-center mb-6">Phygital adds a preference layer after they book</p>
            
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {wellnessConfig.bookingFlow.map((step, idx) => (
                <div key={step} className="text-center">
                  <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-2">
                    <span className="text-emerald-600 font-bold">{idx + 1}</span>
                  </div>
                  <p className="font-medium text-sm">{step}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Event Demo Content - Using PILOT_CONFIGS data
  if (pilotType === "event") {
    const s = settings || {};
    const eventConfig = PILOT_CONFIGS.event;
    
    return (
      <section className="py-12 px-4 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          
          {/* Intro */}
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold mb-3">
              {s.demo_section_title || eventConfig.description}
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Know your guests before they arrive. Collect dietary restrictions, accessibility needs, and preferences all in one place.
            </p>
            <div className="flex flex-wrap justify-center gap-2 mt-6">
              {["RSVP tracking", "VIP management", "Dietary info", "Check-in system"].map(tag => (
                <Badge key={tag} variant="secondary" className="bg-amber-100 text-amber-700">
                  {tag}
                </Badge>
              ))}
            </div>
          </div>

          {/* Feature 1: Guest Management */}
          <div className="bg-white rounded-2xl p-6 shadow-sm border mb-8">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center">
                <Users className="w-4 h-4 text-amber-600" />
              </div>
              <h3 className="text-lg font-bold">{s.demo_card1_title || "Guest Management & VIP Lists"}</h3>
            </div>
            <p className="text-gray-500 text-sm mb-4">
              {s.demo_card1_subtitle || "Track RSVPs, VIPs, and special requirements"}
            </p>
            
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-gray-50 rounded-xl p-4">
                <p className="text-xs text-gray-500 mb-2 font-medium">GUEST SHARES</p>
                <div className="space-y-2">
                  {eventConfig.clientPreferences.map((pref) => (
                    <div key={pref} className="flex items-center gap-2 text-sm">
                      <Check className="w-4 h-4 text-amber-500" />
                      <span>{pref}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="bg-gray-50 rounded-xl p-4">
                <p className="text-xs text-gray-500 mb-2 font-medium">YOU SEE</p>
                <div className="bg-white rounded-lg p-3 border">
                  <p className="text-sm font-medium mb-1">Event Dashboard</p>
                  <p className="text-xs text-gray-500 mb-2">Saturday Launch Party</p>
                  <div className="flex flex-wrap gap-1">
                    {["42 Confirmed", "8 VIP", "5 Dietary"].map(tag => (
                      <Badge key={tag} variant="outline" className="text-xs">{tag}</Badge>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Feature 2: Services/Features */}
          <div className="bg-white rounded-2xl p-6 shadow-sm border mb-8">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center">
                <ClipboardList className="w-4 h-4 text-orange-600" />
              </div>
              <h3 className="text-lg font-bold">{s.demo_card2_title || "Event Features"}</h3>
            </div>
            <p className="text-gray-500 text-sm mb-4">
              {s.demo_card2_subtitle || "Everything you need to manage your event"}
            </p>
            
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
              {eventConfig.services.map((service) => (
                <div key={service.name} className="bg-gray-50 rounded-xl p-3 border">
                  <p className="font-medium text-sm">{service.name}</p>
                  <p className="text-xs text-amber-600">{service.price}</p>
                </div>
              ))}
            </div>
            
            <div>
              <p className="font-medium text-sm mb-2">Available Add-ons</p>
              <div className="flex flex-wrap gap-2">
                {eventConfig.addOns.map((addon) => (
                  <span key={addon} className="px-3 py-1.5 rounded-full text-xs bg-amber-50 text-amber-700">+ {addon}</span>
                ))}
              </div>
            </div>
          </div>

          {/* Feature 3: Real-time Check-in */}
          <div className="bg-white rounded-2xl p-6 shadow-sm border mb-8">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle2 className="w-4 h-4 text-green-600" />
              </div>
              <h3 className="text-lg font-bold">{s.demo_card3_title || "Real-time Check-in"}</h3>
            </div>
            <p className="text-gray-500 text-sm mb-4">
              {s.demo_card3_subtitle || "Digital check-in with Apple Wallet passes and live attendance"}
            </p>
            
            <div className="bg-gray-50 rounded-xl p-4 max-w-md mx-auto">
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm font-medium">Live Check-in</p>
                <Badge className="bg-green-100 text-green-700">Live</Badge>
              </div>
              <div className="space-y-2">
                {[
                  { name: "Sarah M.", time: "Just now", vip: true },
                  { name: "John D.", time: "2 min ago", vip: false },
                  { name: "Michelle K.", time: "5 min ago", vip: true }
                ].map(guest => (
                  <div key={guest.name} className="flex items-center justify-between p-2 bg-white rounded border text-sm">
                    <div className="flex items-center gap-2">
                      <span>{guest.name}</span>
                      {guest.vip && <Badge variant="outline" className="text-xs text-amber-600">VIP</Badge>}
                    </div>
                    <span className="text-xs text-gray-400">{guest.time}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Notifications */}
          <div className="bg-gradient-to-r from-amber-600 to-orange-600 rounded-2xl p-6 text-white mb-8">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                <Bell className="w-4 h-4 text-white" />
              </div>
              <h3 className="text-lg font-bold">Guest Updates? You'll Know</h3>
            </div>
            <p className="text-white/80 text-sm mb-4">
              Get notified when guests update their RSVP, dietary needs, or plus-one info.
            </p>
            <div className="bg-white/10 rounded-xl p-3">
              <p className="text-sm">⚠️ Sarah updated: "Changed to vegetarian, bringing +1"</p>
            </div>
          </div>

          {/* Workflow */}
          <div className="bg-white rounded-2xl p-6 shadow-sm border">
            <h3 className="text-lg font-bold text-center mb-2">Simple Event Flow</h3>
            <p className="text-gray-500 text-sm text-center mb-6">From invite to follow-up</p>
            
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {eventConfig.bookingFlow.map((step, idx) => (
                <div key={step} className="text-center">
                  <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-2">
                    <span className="text-amber-600 font-bold">{idx + 1}</span>
                  </div>
                  <p className="font-medium text-sm">{step}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    );
  }

  // ==========================================================================
  // INSTRUCTOR DEMO - Matches phygital.systems/instructor exactly
  // ==========================================================================
  if (pilotType === "instructor") {
    return (
      <section className="py-12 px-4 bg-white">
        <div className="max-w-5xl mx-auto">
          
          {/* Hero */}
          <div className="text-center mb-16">
            <p className="text-purple-600 text-sm font-medium mb-2">For Educators, Workshop Leaders & Speakers</p>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              {s.demo_hero_title || "teaching preferences, perfectly communicated."}
            </h2>
            <p className="text-gray-600 max-w-xl mx-auto mb-6">
              {s.demo_hero_subtitle || "Know your students' learning styles, skill levels, and goals before the first session. Transform how you teach."}
            </p>
            <div className="flex justify-center gap-3">
              <Button className="bg-purple-600 hover:bg-purple-700" onClick={handleCTA}>
                Accept Invite <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>

          {/* Built for every type of educator */}
          <div className="mb-16">
            <h3 className="text-xl font-bold text-center mb-2">Built for every type of educator</h3>
            <p className="text-gray-500 text-center mb-8">Whether you teach 1-on-1 or to thousands, phygital adapts to your teaching style.</p>
            
            {/* Educator Type Tabs */}
            <div className="flex flex-wrap justify-center gap-2 mb-8">
              {["Workshop Leaders", "1-on-1 Instructors", "Speakers & Presenters", "Community Educators"].map((type, idx) => (
                <span key={type} className={`px-4 py-2 rounded-full text-sm ${idx === 0 ? "bg-purple-600 text-white" : "bg-gray-100 text-gray-700"}`}>
                  {type}
                </span>
              ))}
            </div>

            {/* Workshop Leaders Content */}
            <div className="bg-gray-50 rounded-2xl p-8">
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <p className="text-gray-600 mb-4">Teaching hands-on skills to groups</p>
                  
                  <div className="mb-6">
                    <p className="text-xs text-gray-500 mb-2">Perfect for</p>
                    <div className="flex flex-wrap gap-2">
                      {["Beauty educators", "Craft instructors", "Culinary classes", "DIY workshops"].map(item => (
                        <span key={item} className="px-3 py-1 rounded-full text-xs bg-white">{item}</span>
                      ))}
                    </div>
                  </div>
                  
                  <div>
                    <p className="text-xs text-gray-500 mb-2">Key features</p>
                    <div className="space-y-2">
                      {["Class capacity management", "Skill level matching", "Material lists & prep guides", "Progress tracking per student"].map(feature => (
                        <div key={feature} className="flex items-center gap-2 text-sm">
                          <CheckCircle2 className="w-4 h-4 text-purple-500" />
                          {feature}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                
                <div className="bg-white rounded-xl p-4 border">
                  <p className="text-xs text-gray-500 mb-3">Student Preference Fields</p>
                  <div className="space-y-3">
                    <div>
                      <p className="text-sm font-medium mb-1">Experience level</p>
                      <div className="flex gap-2">
                        {["Beginner", "Intermediate", "Advanced"].map((level, i) => (
                          <span key={level} className={`px-2 py-1 rounded text-xs ${i === 1 ? "bg-purple-100 text-purple-700" : "bg-gray-100"}`}>{level}</span>
                        ))}
                      </div>
                    </div>
                    <div>
                      <p className="text-sm font-medium mb-1">Learning style</p>
                      <div className="flex gap-2">
                        {["Visual", "Hands-on", "Reading"].map((style, i) => (
                          <span key={style} className={`px-2 py-1 rounded text-xs ${i === 0 ? "bg-purple-100 text-purple-700" : "bg-gray-100"}`}>{style}</span>
                        ))}
                      </div>
                    </div>
                    <div>
                      <p className="text-sm font-medium mb-1">Goals</p>
                      <div className="flex gap-2">
                        {["Hobby", "Career change", "Skill upgrade"].map((goal, i) => (
                          <span key={goal} className={`px-2 py-1 rounded text-xs ${i === 2 ? "bg-purple-100 text-purple-700" : "bg-gray-100"}`}>{goal}</span>
                        ))}
                      </div>
                    </div>
                    <div>
                      <p className="text-sm font-medium mb-1">Class format</p>
                      <div className="flex gap-2">
                        {["In-person", "Virtual", "Hybrid"].map((format, i) => (
                          <span key={format} className={`px-2 py-1 rounded text-xs ${i === 0 ? "bg-purple-100 text-purple-700" : "bg-gray-100"}`}>{format}</span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* How it works */}
          <div className="mb-16">
            <h3 className="text-xl font-bold text-center mb-2">How it works</h3>
            <p className="text-gray-500 text-center mb-8">A seamless experience for both educators and learners</p>
            
            <div className="grid md:grid-cols-3 gap-6">
              {[
                { num: "1", title: "Set up your profile", desc: "Define your teaching style, services, and what you need to know about students" },
                { num: "2", title: "Students share preferences", desc: "Learners fill out their preference card with learning style, goals, and context" },
                { num: "3", title: "Teach with context", desc: "Start every session knowing exactly how to help them succeed" }
              ].map((step) => (
                <div key={step.num} className="text-center">
                  <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center mx-auto mb-3">
                    <span className="text-purple-600 font-bold text-lg">{step.num}</span>
                  </div>
                  <h4 className="font-bold mb-2">{step.title}</h4>
                  <p className="text-sm text-gray-600">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Why educators love phygital */}
          <div className="mb-16">
            <h3 className="text-xl font-bold text-center mb-8">Why educators love phygital</h3>
            
            <div className="grid md:grid-cols-3 gap-6">
              {[
                { title: "Personalized from day one", desc: "No more guessing. Know their learning style, goals, and challenges before you meet.", icon: Heart },
                { title: "Track progress", desc: "Document milestones, share feedback, and celebrate wins together.", icon: Star },
                { title: "Build your reputation", desc: "Students remember educators who truly understand them.", icon: Users }
              ].map((feature) => (
                <div key={feature.title} className="bg-gray-50 rounded-xl p-6 text-center">
                  <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center mx-auto mb-3">
                    <feature.icon className="w-6 h-6 text-purple-600" />
                  </div>
                  <h4 className="font-bold mb-2">{feature.title}</h4>
                  <p className="text-sm text-gray-600">{feature.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* CTA */}
          <div className="bg-gradient-to-r from-purple-600 to-indigo-600 rounded-2xl p-8 text-center text-white">
            <h3 className="text-xl font-bold mb-2">Ready to transform how you teach?</h3>
            <p className="text-white/80 mb-4">Get early access and help us build the future of education.</p>
            <Button className="bg-white text-purple-600 hover:bg-white/90" onClick={handleCTA}>
              Accept Invite <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </section>
    );
  }

  // ==========================================================================
  // CULINARY DEMO - Matches phygital.systems/culinary exactly
  // ==========================================================================
  if (pilotType === "culinary") {
    return (
      <section className="py-12 px-4 bg-white">
        <div className="max-w-5xl mx-auto">
          
          {/* Hero */}
          <div className="text-center mb-16">
            <p className="text-orange-600 text-sm font-medium mb-2">For Private Chefs & Bakers</p>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              {s.demo_hero_title || "Every guest's preferences, perfectly captured"}
            </h2>
            <p className="text-gray-600 max-w-xl mx-auto mb-6">
              {s.demo_hero_subtitle || "Dietary restrictions, allergies, cuisine preferences, spice tolerance - all captured upfront."}
            </p>
            <Button className="bg-orange-600 hover:bg-orange-700" onClick={handleCTA}>
              Accept Invite <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>

          {/* Built for every culinary service */}
          <div className="mb-16">
            <h3 className="text-xl font-bold text-center mb-8">Built for every culinary service</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { title: "Private Chef", desc: "In-home dining experiences", icon: ChefHat },
                { title: "Custom Cakes", desc: "Birthday, wedding, celebration cakes", icon: Cake },
                { title: "Event Catering", desc: "Corporate & private events", icon: Users },
                { title: "Meal Prep", desc: "Weekly meal prep services", icon: Calendar }
              ].map((service) => (
                <div key={service.title} className="bg-gray-50 rounded-xl p-6 text-center">
                  <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center mx-auto mb-3">
                    <service.icon className="w-6 h-6 text-orange-600" />
                  </div>
                  <h4 className="font-bold mb-1">{service.title}</h4>
                  <p className="text-xs text-gray-600">{service.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Stop guessing. Start knowing. */}
          <div className="bg-orange-50 rounded-2xl p-8 mb-16">
            <h3 className="text-xl font-bold text-center mb-2">Stop guessing. Start knowing.</h3>
            <p className="text-gray-600 text-center mb-8">Every dietary restriction and preference captured before you start cooking.</p>
            
            <div className="grid md:grid-cols-2 gap-6">
              {[
                { title: "Client taste profiles", desc: "Dietary restrictions, allergies, cuisine preferences, spice tolerance - all captured upfront" },
                { title: "Menu planning", desc: "Share custom menus, get approvals, and track dietary needs for every event" },
                { title: "Service customization", desc: "In-home dinners, event catering, cake orders - manage it all in one place" },
                { title: "Guest management", desc: "Track preferences for each guest at multi-person events" }
              ].map((feature) => (
                <div key={feature.title} className="bg-white rounded-xl p-5">
                  <h4 className="font-bold mb-2">{feature.title}</h4>
                  <p className="text-sm text-gray-600">{feature.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* What you'll know about every client */}
          <div className="mb-16">
            <h3 className="text-xl font-bold text-center mb-2">What you'll know about every client</h3>
            <p className="text-gray-500 text-center mb-8">Detailed preferences collected automatically</p>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-white rounded-xl p-4 border">
                <p className="text-xs text-gray-500 mb-2">Dietary</p>
                <div className="flex flex-wrap gap-1">
                  {["Vegetarian", "Vegan", "Gluten-Free", "Keto", "Halal", "Kosher"].map(item => (
                    <span key={item} className="px-2 py-1 rounded text-xs bg-green-50 text-green-700">{item}</span>
                  ))}
                </div>
              </div>
              <div className="bg-white rounded-xl p-4 border">
                <p className="text-xs text-gray-500 mb-2">Allergies</p>
                <div className="flex flex-wrap gap-1">
                  {["Nuts", "Shellfish", "Dairy", "Eggs", "Soy", "Wheat"].map(item => (
                    <span key={item} className="px-2 py-1 rounded text-xs bg-red-50 text-red-700">{item}</span>
                  ))}
                </div>
              </div>
              <div className="bg-white rounded-xl p-4 border">
                <p className="text-xs text-gray-500 mb-2">Cuisine</p>
                <div className="flex flex-wrap gap-1">
                  {["Italian", "French", "Asian Fusion", "Mediterranean", "Southern", "Mexican"].map(item => (
                    <span key={item} className="px-2 py-1 rounded text-xs bg-blue-50 text-blue-700">{item}</span>
                  ))}
                </div>
              </div>
              <div className="bg-white rounded-xl p-4 border">
                <p className="text-xs text-gray-500 mb-2">Preferences</p>
                <div className="flex flex-wrap gap-1">
                  {["Spicy", "Mild", "No Cilantro", "Extra Garlic", "Low Sodium", "Organic"].map(item => (
                    <span key={item} className="px-2 py-1 rounded text-xs bg-purple-50 text-purple-700">{item}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Custom cake orders, simplified */}
          <div className="bg-gray-50 rounded-2xl p-8 mb-16">
            <h3 className="text-xl font-bold text-center mb-2">Custom cake orders, simplified</h3>
            <p className="text-gray-600 text-center mb-8">Know exactly what your client wants before they place the order. Flavor preferences, allergies, design inspiration - all in one place.</p>
            
            <div className="max-w-md mx-auto bg-white rounded-xl p-6 border">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="font-bold">New Order: Sarah's Birthday Cake</p>
                  <p className="text-xs text-gray-500">Due: March 15, 2026</p>
                </div>
                <Badge className="bg-orange-100 text-orange-700">New</Badge>
              </div>
              
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <p className="text-xs text-gray-500">Flavor</p>
                  <p className="font-medium text-sm">Red Velvet</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Filling</p>
                  <p className="font-medium text-sm">Cream Cheese</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Dietary</p>
                  <p className="font-medium text-sm text-red-600">Nut-Free</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Style</p>
                  <p className="font-medium text-sm">Floral, Elegant</p>
                </div>
              </div>
              
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-500 mb-2">Inspo photos attached (3)</p>
                <div className="flex gap-2">
                  {[1, 2, 3].map(i => (
                    <div key={i} className="w-12 h-12 rounded bg-gradient-to-br from-orange-200 to-pink-200" />
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="bg-gradient-to-r from-orange-600 to-red-600 rounded-2xl p-8 text-center text-white">
            <h3 className="text-xl font-bold mb-2">Ready to elevate your culinary business?</h3>
            <p className="text-white/80 mb-4">Join our pilot program and help shape the future of personalized culinary experiences.</p>
            <Button className="bg-white text-orange-600 hover:bg-white/90" onClick={handleCTA}>
              Accept Invite <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </section>
    );
  }

  // Generic Demo Content for any other pilot type (dispensary, travel, etc.)
  // Uses PILOT_CONFIGS data to generate content dynamically
  const genericConfig = PILOT_CONFIGS[pilotType];
  if (genericConfig) {
    const s = settings || {};
    
    return (
      <section className="py-12 px-4 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          
          {/* Intro */}
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold mb-3">
              {s.demo_section_title || genericConfig.description}
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              {genericConfig.description}
            </p>
            <div className="flex flex-wrap justify-center gap-2 mt-6">
              {genericConfig.features.slice(0, 4).map(feature => (
                <Badge key={feature} variant="secondary" className={`bg-gradient-to-r ${genericConfig.bgLight}`}>
                  {feature}
                </Badge>
              ))}
            </div>
          </div>

          {/* Feature 1: Client Preferences */}
          <div className="bg-white rounded-2xl p-6 shadow-sm border mb-8">
            <div className="flex items-center gap-2 mb-2">
              <div className={`w-8 h-8 rounded-full bg-gradient-to-r ${genericConfig.bgLight} flex items-center justify-center`}>
                <genericConfig.icon className="w-4 h-4" />
              </div>
              <h3 className="text-lg font-bold">{s.demo_card1_title || "Client Preferences"}</h3>
            </div>
            <p className="text-gray-500 text-sm mb-4">
              {s.demo_card1_subtitle || "Know what your clients want before they arrive"}
            </p>
            
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-gray-50 rounded-xl p-4">
                <p className="text-xs text-gray-500 mb-2 font-medium">CLIENT SHARES</p>
                <div className="space-y-2">
                  {genericConfig.clientPreferences.map((pref) => (
                    <div key={pref} className="flex items-center gap-2 text-sm">
                      <Check className="w-4 h-4 text-green-500" />
                      <span>{pref}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="bg-gray-50 rounded-xl p-4">
                <p className="text-xs text-gray-500 mb-2 font-medium">YOU SEE</p>
                <div className="bg-white rounded-lg p-3 border">
                  <p className="text-sm font-medium mb-1">Client Name - Time</p>
                  <p className="text-xs text-gray-500 mb-2">{genericConfig.services[0]?.name || "Service"}</p>
                  <div className="flex flex-wrap gap-1">
                    {genericConfig.clientPreferences.slice(0, 3).map(pref => (
                      <Badge key={pref} variant="outline" className="text-xs">{pref}</Badge>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Feature 2: Services */}
          <div className="bg-white rounded-2xl p-6 shadow-sm border mb-8">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center">
                <ClipboardList className="w-4 h-4 text-purple-600" />
              </div>
              <h3 className="text-lg font-bold">{s.demo_card2_title || "Your Services"}</h3>
            </div>
            <p className="text-gray-500 text-sm mb-4">
              {s.demo_card2_subtitle || "Browse your offerings"}
            </p>
            
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
              {genericConfig.services.map((service) => (
                <div key={service.name} className="bg-gray-50 rounded-xl p-3 border">
                  <p className="font-medium text-sm">{service.name}</p>
                  {/* Show pricing for all EXCEPT makeup */}
                  {pilotType !== 'makeup' && pilotType !== 'mua' && service.price && (
                    <p className={`text-xs bg-gradient-to-r ${genericConfig.color} bg-clip-text text-transparent`}>{service.price}</p>
                  )}
                </div>
              ))}
            </div>
            
            {genericConfig.addOns && genericConfig.addOns.length > 0 && (
              <div>
                <p className="font-medium text-sm mb-2">Available Add-ons</p>
                <div className="flex flex-wrap gap-2">
                  {genericConfig.addOns.map((addon) => (
                    <span key={addon} className={`px-3 py-1.5 rounded-full text-xs bg-gradient-to-r ${genericConfig.bgLight}`}>+ {addon}</span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* CTA */}
          <div className={`bg-gradient-to-r ${genericConfig.color} rounded-2xl p-6 text-white`}>
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                <Bell className="w-4 h-4 text-white" />
              </div>
              <h3 className="text-lg font-bold">Stay Connected</h3>
            </div>
            <p className="text-white/80 text-sm">
              Get notified when clients update their preferences or have questions.
            </p>
          </div>

          {/* Workflow */}
          {genericConfig.bookingFlow && (
            <div className="bg-white rounded-2xl p-6 shadow-sm border mt-8">
              <h3 className="text-lg font-bold text-center mb-2">Simple Flow</h3>
              <p className="text-gray-500 text-sm text-center mb-6">How it works</p>
              
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                {genericConfig.bookingFlow.map((step, idx) => (
                  <div key={idx} className="text-center">
                    <div className={`w-10 h-10 rounded-full bg-gradient-to-r ${genericConfig.bgLight} flex items-center justify-center mx-auto mb-2`}>
                      <span className="font-bold">{idx + 1}</span>
                    </div>
                    <p className="font-medium text-sm">{typeof step === 'object' ? step.step : step}</p>
                    {typeof step === 'object' && step.desc && <p className="text-xs text-gray-500 mt-1">{step.desc}</p>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>
    );
  }

  // Default fallback - no demo available
  return null;
}

// ============================================================================
// FULL PILOT DEMO - Comprehensive demo content for each pilot type
// Shows full platform capabilities for the "Full Demo" tab
// ============================================================================
function FullPilotDemo({ pilotType, config }) {
  return (
    <div className="py-8 px-4">
      <div className="max-w-6xl mx-auto space-y-8">
        {/* Dashboard Preview */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border">
          <h3 className="text-xl font-bold mb-2 flex items-center gap-2">
            <Monitor className="w-5 h-5 text-purple-600" />
            Your Provider Dashboard
          </h3>
          <p className="text-gray-600 mb-6">A glimpse of what you'll have access to</p>
          
          {/* Mock Dashboard */}
          <div className="bg-gray-100 rounded-xl p-4 border">
            {/* Mock Browser Bar */}
            <div className="bg-white rounded-t-lg px-4 py-2 flex items-center gap-2 border-b">
              <div className="flex gap-1.5">
                <div className="w-3 h-3 rounded-full bg-red-400" />
                <div className="w-3 h-3 rounded-full bg-yellow-400" />
                <div className="w-3 h-3 rounded-full bg-green-400" />
              </div>
              <div className="flex-1 bg-gray-100 rounded-full px-3 py-1 text-xs text-gray-500 text-center">
                phygital.systems/dashboard
              </div>
            </div>
            
            {/* Mock Dashboard Content */}
            <div className="bg-white rounded-b-lg p-6">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                {/* Stats Cards */}
                {[
                  { label: "Clients This Month", value: "24", change: "+12%" },
                  { label: "Revenue (MTD)", value: "$3,240", change: "+8%" },
                  { label: "Repeat Rate", value: "68%", change: "+5%" },
                  { label: "Avg Rating", value: "4.9", change: "★" }
                ].map((stat, idx) => (
                  <div key={idx} className="bg-gray-50 rounded-xl p-4 border">
                    <p className="text-2xl font-bold">{stat.value}</p>
                    <p className="text-xs text-gray-500">{stat.label}</p>
                    <span className="text-xs text-green-600">{stat.change}</span>
                  </div>
                ))}
              </div>
              
              {/* Today's Schedule */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold mb-3 flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-blue-600" />
                    Today's Schedule
                  </h4>
                  <div className="space-y-2">
                    {[
                      { time: "9:00 AM", client: "Sarah M.", service: config.services?.[0]?.name || "Service 1" },
                      { time: "11:30 AM", client: "Jennifer K.", service: config.services?.[1]?.name || "Service 2" },
                      { time: "2:00 PM", client: "Amanda R.", service: config.services?.[2]?.name || "Service 3" },
                      { time: "4:30 PM", client: "Michelle T.", service: config.services?.[0]?.name || "Service 1" }
                    ].map((apt, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <span className="text-xs font-medium text-blue-600 w-16">{apt.time}</span>
                          <span className="font-medium text-sm">{apt.client}</span>
                        </div>
                        <span className="text-xs text-gray-500">{apt.service}</span>
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* Client Preferences Preview */}
                <div>
                  <h4 className="font-semibold mb-3 flex items-center gap-2">
                    <Users className="w-4 h-4 text-purple-600" />
                    Client Preferences (Auto-Synced)
                  </h4>
                  <div className="bg-purple-50 rounded-xl p-4 border border-purple-100">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-pink-300 to-purple-300" />
                      <div>
                        <p className="font-medium text-sm">Sarah M.</p>
                        <p className="text-xs text-gray-500">9:00 AM - {config.services?.[0]?.name || "Service"}</p>
                      </div>
                    </div>
                    <div className="space-y-2">
                      {(config.clientPreferences || ["Preference 1", "Preference 2", "Preference 3"]).slice(0, 4).map((pref, idx) => (
                        <div key={idx} className="flex items-center gap-2 text-sm">
                          <CheckCircle2 className="w-4 h-4 text-green-500" />
                          <span className="text-gray-700">{pref}:</span>
                          <span className="text-purple-600">Synced ✓</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Service Menu Builder */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border">
          <h3 className="text-xl font-bold mb-2 flex items-center gap-2">
            <config.icon className="w-5 h-5 text-purple-600" />
            Service Menu Builder
          </h3>
          <p className="text-gray-600 mb-6">Create and manage your services with add-ons{pilotType !== 'makeup' && pilotType !== 'mua' ? ' and pricing' : ''}</p>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            {(config.services || []).map((service, idx) => (
              <div key={idx} className="bg-gray-50 rounded-xl p-4 border hover:border-purple-300 transition-colors">
                <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${config.bgLight} flex items-center justify-center mb-2`}>
                  <config.icon className="w-4 h-4 text-gray-600" />
                </div>
                <p className="font-medium text-sm">{service.name}</p>
                {/* Show pricing for all categories EXCEPT makeup */}
                {pilotType !== 'makeup' && pilotType !== 'mua' && service.price && (
                  <p className={`text-sm bg-gradient-to-r ${config.color} bg-clip-text text-transparent font-semibold`}>{service.price}</p>
                )}
              </div>
            ))}
          </div>
          
          {/* Add-ons */}
          <div>
            <p className="font-medium text-sm mb-2">Available Add-ons:</p>
            <div className="flex flex-wrap gap-2">
              {(config.addOns || []).map((addon, idx) => (
                <span key={idx} className="px-3 py-1.5 rounded-full text-xs bg-purple-100 text-purple-700">
                  + {addon}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Booking Flow */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border">
          <h3 className="text-xl font-bold mb-2">Client Booking Flow</h3>
          <p className="text-gray-600 mb-6">How clients discover, book, and communicate their preferences</p>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {(config.bookingFlow || []).map((step, idx) => {
              const stepData = typeof step === 'object' ? step : { step, desc: '' };
              return (
                <div key={idx} className="text-center">
                  <div className={`w-12 h-12 rounded-full bg-gradient-to-br ${config.bgLight} flex items-center justify-center mx-auto mb-2 border-2 ${idx === 0 ? 'border-purple-400' : 'border-gray-200'}`}>
                    <span className={`font-bold ${idx === 0 ? 'text-purple-600' : 'text-gray-500'}`}>{idx + 1}</span>
                  </div>
                  <p className="text-sm font-medium">{stepData.step}</p>
                  {stepData.desc && <p className="text-xs text-gray-500 mt-1">{stepData.desc}</p>}
                </div>
              );
            })}
          </div>
        </div>

        {/* Client Communication Tools */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border">
          <h3 className="text-xl font-bold mb-2 flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-purple-600" />
            Client Communication
          </h3>
          <p className="text-gray-600 mb-6">Automated reminders, preference collection, and follow-ups</p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Reminder Templates */}
            <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
              <h4 className="font-semibold text-sm mb-2 text-blue-700 flex items-center gap-2">
                <Send className="w-4 h-4" />
                Automated Reminders
              </h4>
              <ul className="space-y-1 text-xs text-gray-600">
                <li>- 24hr before appointment</li>
                <li>- Day-of morning reminder</li>
                <li>- Address release on confirmation</li>
              </ul>
            </div>
            
            {/* Preference Forms */}
            <div className="bg-purple-50 rounded-xl p-4 border border-purple-100">
              <h4 className="font-semibold text-sm mb-2 text-purple-700 flex items-center gap-2">
                <ClipboardList className="w-4 h-4" />
                Preference Forms
              </h4>
              <ul className="space-y-1 text-xs text-gray-600">
                <li>- First-time client intake</li>
                <li>- Inspiration photo upload</li>
                <li>- Allergy/sensitivity tracking</li>
              </ul>
            </div>
            
            {/* Post-Service */}
            <div className="bg-green-50 rounded-xl p-4 border border-green-100">
              <h4 className="font-semibold text-sm mb-2 text-green-700 flex items-center gap-2">
                <Star className="w-4 h-4" />
                Post-Service
              </h4>
              <ul className="space-y-1 text-xs text-gray-600">
                <li>- Thank you message</li>
                <li>- Review request</li>
                <li>- Rebooking nudge</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Portfolio & Marketing */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border">
          <h3 className="text-xl font-bold mb-2 flex items-center gap-2">
            <Instagram className="w-5 h-5 text-purple-600" />
            Portfolio & Marketing
          </h3>
          <p className="text-gray-600 mb-6">Showcase your work and generate shareable content</p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Portfolio Grid */}
            <div>
              <h4 className="font-semibold text-sm mb-3">Work Portfolio</h4>
              <div className="grid grid-cols-3 gap-2">
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <div key={i} className={`aspect-square rounded-lg bg-gradient-to-br ${config.bgLight}`} />
                ))}
              </div>
            </div>
            
            {/* IG Story Generator */}
            <div className={`bg-gradient-to-br ${config.bgLight} rounded-xl p-4 border`}>
              <h4 className="font-semibold text-sm mb-2">IG Story Generator</h4>
              <p className="text-xs text-gray-600 mb-3">Generate beautiful story templates with your services</p>
              <div className="flex gap-2">
                <span className="px-2 py-1 rounded text-xs bg-white/50">Show Prices</span>
                <span className="px-2 py-1 rounded text-xs bg-white/50">Add Promos</span>
                <span className="px-2 py-1 rounded text-xs bg-white/50">1080x1920</span>
              </div>
            </div>
          </div>
        </div>

        {/* Features Grid */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border">
          <h3 className="text-xl font-bold mb-6">All Features Included</h3>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { title: "Client Profiles", desc: "Auto-synced preferences" },
              { title: "Smart Scheduling", desc: "Integrated calendar" },
              { title: "Payment Tracking", desc: "Tips, deposits, invoices" },
              { title: "Analytics", desc: "Revenue & client insights" },
              { title: "Portfolio", desc: "Showcase your work" },
              { title: "Reviews", desc: "Collect testimonials" },
              { title: "IG Integration", desc: "Connect your feed" },
              { title: "Mobile App", desc: "Manage on the go" }
            ].map((feature, idx) => (
              <div key={idx} className="flex items-start gap-2">
                <CheckCircle2 className="w-5 h-5 text-green-500 shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-sm">{feature.title}</p>
                  <p className="text-xs text-gray-500">{feature.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
